import { isDetectedPreset, type DetectedPreset } from '@/core/preset-selection'
import { enabledBuffCount } from '@/buffs/types'
import type { CalibrationInitializationOptions, CalibrationProgress } from '@/core/layout-calibration'
import { performanceDiagnostics } from '@/services/performance-diagnostics'
import { ExpRecognitionProgressTracker, needsExpRecheck } from '@/core/exp-recognition-progress'
import type { SessionSamplingStage } from '@/services/analysis-pipeline'
import { computed, markRaw, onScopeDispose, ref, shallowRef, watch } from 'vue'
import { defineStore } from 'pinia'
import { getBuffOverlay } from '@/buffs/overlay-source'
import { hasCaptureConsumer, retainCapture, releaseCapture, requestedCaptureFps } from '@/services/capture-consumers'
import { ElMessage } from 'element-plus'
import { cloneCaptureProfile } from '@/core/capture-profile'
import { calibrationRegionsChanged } from '@/core/layout-calibration'
import {
  SessionStartExpConsensus,
  type SessionStartIssue,
} from '@/core/session-start-baseline'
import { SessionEngine } from '@/core/session-engine'
import { rankSourceRestriction } from '@/core/rank-capture'
import { ExpAdmissionFilter, type ExpAdmissionCheckpoint } from '@/core/exp-admission'
import {
  DEFAULT_CAPTURE_FPS,
  METRIC_RECOGNITION_INTERVAL_VERSION,
  metricRecognitionEnabled,
  migrateRecognitionFrequencies,
  recommendedMetricRecognitionIntervalsMs,
} from '@/core/recognition-settings'
import { recognitionMetricKeys, type CaptureProfile, type ExpExactCandidate, type ExpConfirmationFailureReason, type GameStateSnapshot, type LiveRateSnapshot, type MetricRecognitionFailureReason, type RawReading, type RecognitionMetricKey, type SessionJourney, type SessionRecord, type ValidatedObservation } from '@/domain/types'
import { i18n } from '@/i18n'
import { PresetRegistry } from '@/presets/registry'
import { AutomaticPresetMonitor, validatePresetUi } from '@/services/automatic-preset'
import { captureProfileIdentity, GameDetectionPreparationError, silentGameDetectionFailure, waitForNativePickerExit,
  type CapturePreparationDiagnostic, type CapturePreparationEntry, type GameDetectionFailureReason } from '@/services/capture-preparation'
import { normalizeOcrRecognitionModel, type OcrRecognitionModel } from '@/core/display-settings'
import { mxdcOcrModels } from '@/services/ocr-resource-manifest'
import { VideoFrameSource } from '@/services/video-frame-source'
import { calibrationDelay, healthyStaticCapture } from '@/core/calibration-sampling'
import type { GamePreset, HudLocation, PresetRecognizer } from '@/presets/types'
import { AnalysisPipeline, type PipelineResult } from '@/services/analysis-pipeline'
import { frameStallTimeoutMs } from '@/services/frame-scheduler'
import {
  captureService,
  classifyCaptureFailure,
  CaptureSelectionReplacedError,
  createCaptureDiagnostic,
  type CaptureDiagnostic,
  type CaptureFailurePhase,
  type CaptureFailureKind,
  type CaptureInfo,
  type CaptureMediaState,
  type CaptureSelectionPreference,
  type CaptureStatus,
} from '@/services/capture-service'
import { createRankQueueItems, notifyRankQueueChanged } from '@/services/rank-api'
import { db, saveEndedSessionState, saveSessionState } from '@/services/database'
import { SessionAutosave } from '@/services/session-autosave'
import {
  ensureOcrModelsCached,
  isOcrResourceLoadError,
  type OcrCachePreparationResult,
  type OcrCacheProgress,
} from '@/services/ocr-resource-cache'
import { releaseSharedOcrEngine } from '@/services/ocr-engine'
import { OcrInitializationError, type OcrStartupDiagnostic } from '@/services/ocr-startup'
import { pipService, type PipData } from '@/services/pip-service'
import { classifyPipError, createPipIssue, getPipAvailability, PipOpenError, type PipIssue, type PipIssueCode, type PipOpenResult, type PipWidgetMode } from '@/services/pip-availability'
import { ocrRuntimeBlock, probeIndexedDb } from '@/services/browser-capabilities'
import { finalSamplingBudgetMs, performanceConfigs, recognitionForTier } from '@/core/performance-settings'
import { useAppStore } from './app'
import { blockReload, reloadSafety, saveBeforeReload } from '@/services/reload-safety'
import { InitialLayoutRecognition, withInitialLayoutLock } from '@/services/initial-layout-recognition'

export type OcrStatus = 'idle' | 'checking' | 'downloading' | 'prepared' | 'initializing' | 'ready' | 'error'
export type OcrStage = 'idle' | 'checking' | 'detection' | 'recognition' | 'prepared' | 'initializing' | 'ready'
export type OcrErrorType = 'resource' | 'initialization' | 'unknown' | 'simd' | 'insecure-context' | 'game-detection'
export type CaptureStartupPhase = 'idle' | 'picker' | 'selected' | 'picker-exit' | 'game-detection' | 'resources' | 'worker' | 'video' | 'ready' | 'error'
export interface SessionStartProgress {
  readonly phase: 'confirming' | 'slow'
  readonly stage?: SessionSamplingStage
  readonly slowReason?: 'frame' | 'ocr'
  readonly requestedAt: number
  readonly elapsedMs: number
  readonly confirmedSamples: 0 | 1 | 2
  readonly targetSamples: 2
  readonly currentMetric: 'level' | 'exp'
  readonly candidate?: number
  readonly reviewFailureReason?: MetricRecognitionFailureReason
  readonly confirmationFailureReason?: ExpConfirmationFailureReason
  readonly lastInferenceDurationMs?: number
}

const SESSION_START_BASELINE_MAX_AGE_MS = 2_000
const SLOW_CAPTURE_SELECTION_MS = 15_000
const CAPTURE_STARTUP_PHASE_KEY = 'maple-log:capture-startup-phase'

export const useLiveStore = defineStore('live', () => {
  const app = useAppStore()
  const preset = shallowRef<GamePreset | null>(null)
  const profile = shallowRef<CaptureProfile | null>(null)
  const session = shallowRef<SessionRecord | null>(null)
  const automaticPresetMonitor = new AutomaticPresetMonitor()
  let automaticPresetPreparation: Promise<boolean> | null = null
  let automaticPresetController: AbortController | null = null
  const capturePreparationDiagnostic = shallowRef<CapturePreparationDiagnostic | null>(null)
  const gameTypeChoice = shallowRef<{ reason: 'unknown' | 'unconfirmed' | 'timeout' | 'ambiguous' | 'probe-error'; entry: CapturePreparationEntry } | null>(null)
  const gameTypeChoiceOpen = ref(false)
  const isChoosingGameType = ref(false)
  let resolvedPresetContext = ''
  let resolvedPresetSource: HTMLVideoElement['srcObject'] = null
  let choiceContext = ''
  let choiceSource: HTMLVideoElement['srcObject'] = null

  function rememberPresetContext(): void {
    resolvedPresetContext = presetContext()
    resolvedPresetSource = captureService.video.srcObject
  }

  function presetContext(): string {
    return JSON.stringify([captureService.selectionId, captureService.video.videoWidth,
      captureService.video.videoHeight, captureProfileIdentity(profile.value)])
  }

  function clearGameTypeChoice(): void {
    gameTypeChoice.value = null
    gameTypeChoiceOpen.value = false
    choiceContext = ''
    choiceSource = null
  }

  const liveRates = shallowRef<LiveRateSnapshot | null>(null)
  let expReviewRequest: string | null = null
  let expReviewPipeline: AnalysisPipeline | null = null
  watch([() => session.value?.id, liveRates], ([sessionId, rates]) => {
    const readout = rates?.expReadout
    if (!sessionId || !readout) { expReviewRequest = null; return }
    performanceDiagnostics.observeExpReadout(sessionId, readout)
    performanceDiagnostics.observeExpChart(sessionId, rates?.expChart, readout)
    if (expReviewPipeline !== pipeline) { expReviewPipeline = pipeline; expReviewRequest = null }
    const needsConfirmation = needsExpRecheck(readout, preset.value?.id ?? '')
    if (!needsConfirmation) { expReviewRequest = null; return }
    const key = `${sessionId}:${captureGeneration}:${readout.scope}:${readout.since}`
    if (session.value?.status === 'running' && captureService.status === 'active' && pipeline && key !== expReviewRequest) {
      expReviewRequest = key
      pipeline?.requestExpConfirmation?.()
    }
  })
  const gameState = shallowRef<GameStateSnapshot | null>(null)
  const rawReading = shallowRef<RawReading | null>(null)
  const expRecognitionProgress = new ExpRecognitionProgressTracker()
  const hudLocation = shallowRef<HudLocation | null>(null)
  const captureStatus = ref<CaptureStatus>(captureService.status)
  // Page-lifetime latch: stopping capture or navigating must not revive sponsors.
  const hasSelectedWindowInPage = ref(false)
  const dashboardSponsorsDismissed = ref(false)
  const dashboardMesosHidden = ref(false)
  const dashboardResourcesHidden = ref(false)
  watch(() => [
    !profile.value || metricRecognitionEnabled(profile.value.recognition, 'mesos'),
    !profile.value || metricRecognitionEnabled(profile.value.recognition, 'hp') || metricRecognitionEnabled(profile.value.recognition, 'mp'),
  ], ([mesosEnabled, resourcesEnabled]) => {
    if (mesosEnabled) dashboardMesosHidden.value = false
    if (resourcesEnabled) dashboardResourcesHidden.value = false
  }, { flush: 'sync' })
  watch(captureStatus, status => {
    if (['selected', 'connecting', 'active'].includes(status)) hasSelectedWindowInPage.value = true
  }, { immediate: true, flush: 'sync' })
  const captureLabel = ref('')
  const captureFailure = ref<CaptureFailureKind | null>(null)
  const captureDiagnostic = shallowRef<CaptureDiagnostic | null>(null)
  const capturePreference = ref<CaptureSelectionPreference>('window')
  const captureSelectionPending = ref(false)
  const captureStartupPhase = ref<CaptureStartupPhase>('idle')
  const error = ref<string | null>(null)
  watch(error, message => {
    if (!message) return
    ElMessage({ type: 'error', message, duration: 5_000, showClose: true, grouping: true })
  })
  const pipAvailability = shallowRef(getPipAvailability(undefined, app.pipWindowMode))
  watch(() => app.pipWindowMode, () => refreshPipAvailability())
  const pipSupported = computed(() => pipAvailability.value.supported)
  const pipIssue = shallowRef<PipIssue | null>(null)
  const isOpeningPip = ref(false)
  let pipOpenPromise: Promise<PipOpenResult> | null = null
  let pipSource: 'live' | 'stream' = 'stream'
  const needsCalibration = ref(false)
  const isInitializingCalibration = ref(false)
  const firstUseCalibrationPhase = ref<'idle' | 'waiting' | 'running' | 'result'>('idle')
  const firstUseCalibrationOpen = ref(false)
  const firstUseCalibrationProgress = shallowRef<CalibrationProgress>({ stage: 'preparing', located: [], confirming: [], saved: [] })
  const firstUseCalibrationOutcome = ref<'' | 'complete' | 'partial' | 'failed' | 'saveFailed' | 'cancelled'>('')
  const firstUseCalibrationFrame = shallowRef<ImageData | null>(null)
  const firstUseCalibrationRetained = ref<RecognitionMetricKey[]>([])
  const firstUseCalibrationBlocking = computed(() => firstUseCalibrationPhase.value !== 'idle')
  const firstUseCalibrationBusy = computed(() => firstUseCalibrationPhase.value === 'waiting' || firstUseCalibrationPhase.value === 'running')
  let firstUseController: AbortController | null = null
  let firstUseOperation = 0
  let firstUseStorageWarningShown = false
  const firstUseHistory = new InitialLayoutRecognition(() => {
    if (firstUseStorageWarningShown) return
    firstUseStorageWarningShown = true
    ElMessage({ type: 'warning', message: String(i18n.global.t('firstUseCalibration.storageFailed')), duration: 6_000, showClose: true })
  })
  const ocrStatus = ref<OcrStatus>('idle')
  const ocrStage = ref<OcrStage>('idle')
  const ocrProgress = ref(0)
  const ocrCachedModels = ref(0)
  const ocrError = ref<string | null>(null)
  const ocrErrorType = ref<OcrErrorType | null>(null)
  const ocrStartupDiagnostic = shallowRef<OcrStartupDiagnostic | null>(null)
  const ocrCacheDegraded = ref(false)
  const isSwitchingOcrModel = ref(false)
  const isLoadingPreset = ref(false)
  let presetLoadRevision = 0
  const isStarting = ref(false)
  const isEnding = ref(false)
  const pendingFinalization = shallowRef<Parameters<typeof saveEndedSessionState> | null>(null)
  const hasPendingFinalization = computed(() => pendingFinalization.value !== null)
  const isPreparingForReload = computed(() => reloadSafety.preparing)
  const startIssue = shallowRef<SessionStartIssue | null>(null)
  const sessionStartProgress = shallowRef<SessionStartProgress | null>(null)
  const journey = shallowRef<SessionJourney | null>(null)
  const previewRevision = ref(0)
  const onPreviewChanged = () => {
    previewRevision.value += 1
    if (captureRecoveryController && pipeline && captureService.status === 'active') pipeline.rebindVideo(captureService.video)
  }
  captureService.addEventListener('preview', onPreviewChanged)
  const video = computed(() => {
    previewRevision.value
    return captureService.video
  })
  const autosave = markRaw(new SessionAutosave(lastSaved => {
    const current = session.value
    if (!current || current.status === 'ended' || current.status === 'idle' || isEnding.value) return null
    if (current.status !== 'running' && lastSaved?.session === current && lastSaved.journey === journey.value) return null
    const capturedAt = Date.now()
    if (current.status === 'running' && sessionEngine) session.value = sessionEngine.current(capturedAt)
    return session.value ? { session: session.value, journey: sessionEngine?.journey() ?? journey.value, capturedAt } : null
  }, snapshot => saveSessionState(snapshot.session, snapshot.journey, snapshot.capturedAt)))
  let pipeline: AnalysisPipeline | null = null
  let sessionEngine: SessionEngine | null = null
  let sessionExpCheckpoint: ExpAdmissionCheckpoint | null = null
  let calibrationMetric: RecognitionMetricKey | null = null
  let captureGeneration = 0
  let ocrResourcePreparation: Promise<void> | null = null
  let ocrEngineInitialization: Promise<void> | null = null
  let retainedOcrPreparation: OcrCachePreparationResult | null = null
  let endSessionPromise: Promise<void> | null = null
  let startSessionPromise: Promise<void> | null = null
  let startRequestId = 0
  let startBaselineController: AbortController | null = null
  let cancelFirstUseWait: (() => void) | null = null
  let settlementController: AbortController | null = null
  let liveRateRefreshTimer = 0
  let lastLiveRateRefreshAt = 0
  let mediaContinuityLost = false
  let mediaWaitingTimer: ReturnType<typeof setTimeout> | null = null
  let resumeObservationTimer: ReturnType<typeof setTimeout> | null = null
  let resumeObservationUntil = 0
  const captureRecoveryState = ref<'idle' | 'recovering' | 'confirming' | 'needs-user-action' | 'recognition-stalled'>('idle')
  const captureConfirmationSlow = ref(false)
  let captureRecoveryController: AbortController | null = null
  let recoveryEpisode = false
  let recoveryHealthySince: number | null = null
  let recoveryLastResultAt: number | null = null
  let recoveryLastFrameId: number | null = null
  let recoveryConfirmationSince: number | null = null
  let recoveryConfirmingAt: number | null = null
  const captureConfirmationReason = computed(() => {
    if (captureRecoveryState.value !== 'confirming') return null
    const raw = rawReading.value, boundary = recoveryConfirmationSince ?? Infinity
    const confidence = profile.value?.recognition.minConfidence ?? .62
    const holdMs = liveRates.value?.expReadout?.holdMs ?? 15_000
    const stale = (at: number) => at <= boundary || Date.now() - at > holdMs
    if (!raw || raw.level.value === null || raw.level.confidence < confidence || stale(raw.level.capturedAt)) return 'waitingLevel'
    if (raw.exp.value?.absolute == null || raw.exp.value.ratio == null || raw.exp.value.estimated
      || raw.exp.confidence < confidence || stale(raw.exp.capturedAt)) return 'waitingExp'
    return 'checkingExp'
  })
  const captureEfficiencyWarming = computed(() => captureRecoveryState.value === 'idle'
    && session.value?.status === 'running' && liveRates.value?.expReadout?.scope === 'recovery'
    && liveRates.value.expReadout.confirmation?.status === 'ready' && liveRates.value.expReadout.status === 'recovering')
  let activePipelineGeneration = -1
  let ocrLifecycleRevision = 0
  let preparedOcrFingerprint = ''
  let readyOcrFingerprint = ''
  let cacheDegradedWarningShown = false
  let latestObservation: ValidatedObservation | null = null
  let latestObservationGeneration = -1
  let initialized = false
  let disposed = false
  let cancelOcrProgress: (() => void) | null = null
  const releaseStoreDiagnostic = performanceDiagnostics.acquire('live-stores')
  const stopLongTaskObserver = performanceDiagnostics.observeLongTasks()

  const isCapturing = computed(() => captureStatus.value === 'active')
  const isSelectingWindow = computed(() => captureSelectionPending.value || captureStatus.value === 'requesting'
    || captureStatus.value === 'connecting'
    || (captureStatus.value === 'selected' && ocrStatus.value !== 'error'))
  const captureCompatibilityIssue = computed(() => captureService.compatibilityIssue)
  const captureSupported = computed(() => captureCompatibilityIssue.value === null)
  const canSelectWindow = computed(() => Boolean(preset.value && profile.value && captureSupported.value))
  performanceDiagnostics.setRecordingReader(() => ({
    sessionId: session.value?.id ?? null, status: session.value?.status ?? 'idle',
    startedAt: session.value?.startedAt ?? null, activeDurationMs: session.value?.activeDurationMs ?? null,
    starting: isStarting.value, ending: isEnding.value, startStage: sessionStartProgress.value?.stage ?? null,
    selectionId: captureService.selectionId, captureGeneration, pipelineGeneration: activePipelineGeneration,
    pipelineActive: pipeline !== null, presetId: preset.value?.id ?? null, profileId: profile.value?.id ?? null,
    readoutStatus: liveRates.value?.expReadout?.status ?? null, chartStatus: liveRates.value?.expChart?.recent.status ?? null,
    chartContinuousMs: liveRates.value?.expChart?.recent.continuousMs ?? null,
    chartEndpointAt: liveRates.value?.expChart?.recent.at ?? null,
    captureRecovery: { state: captureRecoveryState.value, confirmingSince: recoveryConfirmingAt, minimumSourceAt: recoveryConfirmationSince,
      slow: captureConfirmationSlow.value, blockingReason: captureConfirmationReason.value,
      failureReason: rawReading.value?.diagnostics.metrics?.exp?.failureReason ?? null,
      confirmation: liveRates.value?.expReadout?.confirmation ?? null },
  }))
  watch([() => session.value?.id, () => session.value?.status, isStarting, isEnding], () => {
    performanceDiagnostics.recordingEvent({ kind: 'session', sessionId: session.value?.id ?? null,
      reason: isEnding.value ? 'ending' : isStarting.value ? 'starting' : session.value?.status ?? 'idle', generation: captureGeneration })
  }, { flush: 'sync' })
  const isRunning = computed(() => session.value?.status === 'running')
  const isPaused = computed(() => session.value?.status === 'paused')

  const ocrModelSwitchBlocked = computed(() => {
    if (session.value && session.value.status !== 'ended') return 'recordingLocked'
    if (!preset.value || isLoadingPreset.value || isSwitchingOcrModel.value || isStarting.value || isEnding.value
      || hasPendingFinalization.value || reloadSafety.preparing || isInitializingCalibration.value
      || firstUseCalibrationBlocking.value || isSelectingWindow.value
      || !['idle', 'ready', 'error'].includes(ocrStatus.value)) return 'busyLocked'
    return null
  })

  watch([captureStatus, () => profile.value?.id, () => reloadSafety.preparing], ([status, id, reloading], previous) => {
    if (status !== 'active' || reloading || previous && id !== previous[1]) invalidateFirstUseCalibration()
  }, { flush: 'sync' })
  onScopeDispose(() => {
    disposed = true
    resetCaptureRecovery()
    performanceDiagnostics.recordingEvent({ kind: 'pipeline', reason: 'disposed', sessionId: session.value?.id ?? null, generation: captureGeneration })
    performanceDiagnostics.setRecordingReader(null)
    performanceDiagnostics.setSamplingReader(null)
    startBaselineController?.abort('disposed')
    automaticPresetController?.abort('disposed')
    releaseStoreDiagnostic()
    stopLongTaskObserver()
    captureService.removeEventListener('preview', onPreviewChanged)
    captureService.removeEventListener('status', onCaptureStatus)
    captureService.removeEventListener('rank-assessment', restrictSessionRankForSource)
    captureService.removeEventListener('ended', onCaptureEnded)
    captureService.removeEventListener('media-state', onCaptureMediaState)
    document.removeEventListener('visibilitychange', handleVisibilityChange)
    document.removeEventListener('freeze', handlePageFreeze)
    document.removeEventListener('resume', handlePageResume)
    window.removeEventListener('pageshow', handlePageResume)
    window.removeEventListener('pagehide', handlePageHide)
    invalidateFirstUseCalibration()
    cancelFirstUseWait?.()
    cancelOcrProgress?.()
    settlementController?.abort()
    stopLiveRateRefresh()
    if (mediaWaitingTimer !== null) clearTimeout(mediaWaitingTimer)
    mediaWaitingTimer = null
    // Only the Pinia scope owns these resources. Route components must not stop them.
    if (sessionEngine && session.value?.status === 'running') {
      session.value = sessionEngine.interrupt(Date.now())
      journey.value = sessionEngine.journey()
    }
    void persistSession()
    void autosave.stop({ discardPending: false })
    void releaseRecognitionForCaptureSelection().catch(() => undefined)
    pipService.close()
    releaseCapture('statistics')
    sessionEngine = null
    latestObservation = null
    rawReading.value = null
  })

  watch([() => app.pipSkin, () => app.rateUnit, () => app.levelEtaMode], () => pipService.refresh())
  const appliedPerformanceTier = ref(app.performanceSettings.tier)
  const captureFpsApplied = ref<number | null>(null)
  const performanceChangeDeferred = computed(() => firstUseCalibrationBusy.value || isStarting.value || isEnding.value || hasPendingFinalization.value)
  const captureFrequencyPending = computed(() => captureFpsApplied.value !== null
    && ['active', 'selected', 'connecting'].includes(captureStatus.value)
    && captureFpsApplied.value !== app.performanceConfig.captureFpsLimit)
  watch([() => app.performanceSettings.tier, performanceChangeDeferred], ([tier, deferred]) => {
    if (deferred || tier === appliedPerformanceTier.value) return
    appliedPerformanceTier.value = tier
    restrictSessionRankForPerformance()
    pipeline?.setProcessingFrameRateLimit?.(performanceConfigs[tier].processingFpsLimit)
    if (profile.value) void updateProfile(profile.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }, { flush: 'sync' })
  watch(() => app.pipStream, () => pipService.refresh(), { deep: true })

  const latestPipLevelUp = computed(() => {
    const current = journey.value
    if (!current) return undefined
    for (let index = current.events.length - 1; index >= 0; index--) {
      const event = current.events[index]!
      if (event.type === 'level-up') return `${current.sessionId}:${event.capturedAt}:${event.fromLevel}:${event.toLevel}`
    }
    return undefined
  })

  const onCaptureStatus: EventListener = event => {
    if (!hasCaptureConsumer('statistics')) { captureStatus.value = 'idle'; return }
    captureStatus.value = (event as CustomEvent<CaptureStatus>).detail
    if (captureStatus.value === 'active') restrictSessionRankForSource()
  }
  const onCaptureEnded = () => {
    if (!hasCaptureConsumer('statistics')) return
    if (captureService.lastDiagnostic?.phase === 'runtime') {
      captureFailure.value = 'source-ended'
      captureDiagnostic.value = captureService.lastDiagnostic
    }
    startBaselineController?.abort('capture-unavailable')
    void interruptCapture().catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }
  const onCaptureMediaState: EventListener = event => {
    if (!hasCaptureConsumer('statistics')) return
    handleCaptureMediaState((event as CustomEvent<CaptureMediaState>).detail)
  }

  async function initialize(): Promise<void> {
    if (initialized || disposed) return
    initialized = true
    try {
      await loadPreset(app.presetId, false)
      if (disposed) return
      captureService.addEventListener('status', onCaptureStatus)
      captureService.addEventListener('rank-assessment', restrictSessionRankForSource)
      captureService.addEventListener('ended', onCaptureEnded)
      captureService.addEventListener('media-state', onCaptureMediaState)
      document.addEventListener('visibilitychange', handleVisibilityChange)
      document.addEventListener('freeze', handlePageFreeze)
      document.addEventListener('resume', handlePageResume)
      window.addEventListener('pageshow', handlePageResume)
      window.addEventListener('pagehide', handlePageHide)
    } catch (caught) {
      initialized = false
      error.value = !probeIndexedDb()
        ? String(i18n.global.t('capability.storage.hint'))
        : caught instanceof Error ? `${caught.name}: ${caught.message}` : String(caught)
      ocrStatus.value = 'error'
      ocrError.value = error.value
      ocrErrorType.value = 'unknown'
      return
    }
  }

  async function loadPreset(id: string, initializeResources = initialized, isCurrent: () => boolean = () => true, cancelled?: Promise<never>): Promise<void> {
    const revision = ++presetLoadRevision
    isLoadingPreset.value = true
    try {
      const loading = loadPresetResources(id, initializeResources, () => revision === presetLoadRevision && isCurrent())
      await (cancelled ? Promise.race([loading, cancelled]) : loading)
    } finally { if (revision === presetLoadRevision) isLoadingPreset.value = false }
  }

  async function loadPresetResources(id: string, initializeResources: boolean, isCurrent: () => boolean): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value || !isCurrent()) return
    startBaselineController?.abort('profile-changed')
    if (session.value && session.value.status !== 'ended') {
      if (session.value.presetId !== id) throw new Error('End the active record before changing presets')
      return
    }
    resolvedPresetContext = ''
    if (!isChoosingGameType.value) clearGameTypeChoice()
    invalidateFirstUseCalibration()
    await firstUseHistory.initialize()
    if (disposed || !isCurrent()) return
    const previousPreset = preset.value
    const previousPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    previousPipeline?.stop()
    const engineRelease = previousPreset
      ? releaseSharedOcrEngine(previousPreset.remoteResources.ocrModels)
      : Promise.resolve()
    previousPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    latestObservation = null
    latestObservationGeneration = -1
    stopLiveRateRefresh()
    await engineRelease
    if (disposed || !isCurrent()) return
    const loadedPreset = withOcrModel(await PresetRegistry.load(id), app.ocrRecognitionModel)
    if (disposed || !isCurrent()) return
    const exactProfiles = await db.profiles.where('[presetId+presetVersion]').equals([id, loadedPreset.version]).toArray()
    const exact = exactProfiles.reduce<CaptureProfile | null>((latest, item) => !latest || item.updatedAt > latest.updatedAt ? item : latest, null)
    const previous = exact ? null : await db.profiles.orderBy('updatedAt').reverse().filter((item) => item.presetId === id).first()
    if (disposed || !isCurrent()) return
    preset.value = loadedPreset
    app.presetId = id
    sessionEngine = new SessionEngine(loadedPreset)
    journey.value = null
    liveRates.value = null
    const migrated = previous ? preset.value.migrateProfile(previous) : null
    needsCalibration.value = Boolean(previous && !migrated)
    const selectedProfile = exact ? preset.value.migrateProfile(exact) ?? createProfile(preset.value) : migrated ?? createProfile(preset.value)
    profile.value = {
      ...selectedProfile,
      recognition: recognitionForTier(migrateRecognitionFrequencies(selectedProfile.recognition), appliedPerformanceTier.value),
    }
    await db.profiles.put(profile.value)
    if (initializeResources && isCapturing.value && isCurrent()) await initializeOcr()
  }

  async function selectWindow(preference: CaptureSelectionPreference = 'window'): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value) return
    if (isEnding.value || isSelectingWindow.value || isSwitchingOcrModel.value) return
    if (!canSelectWindow.value || !preset.value || !profile.value) return
    startBaselineController?.abort('source-replaced')
    automaticPresetController?.abort('source-replaced')
    resolvedPresetContext = ''
    clearGameTypeChoice()
    invalidateFirstUseCalibration()
    resetCaptureRecovery()
    captureSelectionPending.value = true
    capturePreference.value = preference
    const selectionStartedAt = Date.now()
    retainCapture('statistics', app.performanceConfig.captureFpsLimit)
    const selection = captureService.selectWindow(preference, requestedCaptureFps())
    captureFpsApplied.value = app.performanceConfig.captureFpsLimit
    const selectionId = captureService.selectionId
    setCaptureStartupPhase('picker')
    sessionStartProgress.value = null
    startIssue.value = null
    error.value = null
    captureFailure.value = null
    captureDiagnostic.value = null
    captureGeneration += 1
    const interrupted = session.value?.status === 'running'
      ? interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      : Promise.resolve()
    const engineRelease = releaseRecognitionForCaptureSelection()
    try {
      const info = await selection
      if (hasCaptureConsumer('buffs')) captureStatus.value = captureService.status
      if (selectionId !== captureService.selectionId) return
      const selectionDurationMs = Date.now() - selectionStartedAt
      setCaptureStartupPhase('video')
      let connected = info
      let connectedFromSelection = false
      if (captureStatus.value === 'selected' || captureService.status === 'selected') {
        try {
          connected = await captureService.connectSelectedWindow()
          connectedFromSelection = true
        } catch (caught) {
          if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
          setCaptureStartupPhase('error')
          reportCaptureFailure(caught, 'track')
          return
        }
        if (selectionId !== captureService.selectionId) return
      }
      await applyCaptureInfo(connected, selectionId)
      if (selectionId !== captureService.selectionId) return
      if (connectedFromSelection) {
        setCaptureStartupPhase('picker-exit')
        const diagnostic = newPreparationDiagnostic('selection', selectionId)
        publishPreparationDiagnostic(diagnostic)
        const waited = await waitForNativePickerExit()
        diagnostic.stages.pickerExitMs = waited.elapsedMs
        diagnostic.pickerExitFallback = waited.fallback
        const reason = disposed ? 'disposed' : reloadSafety.preparing ? 'reload'
          : selectionId !== captureService.selectionId ? 'source-replaced' : !isCapturing.value ? 'capture-ended' : null
        if (reason) { diagnostic.outcome = 'cancelled'; diagnostic.reason = reason }
        publishPreparationDiagnostic(diagnostic)
        if (reason) return
      }
      await interrupted
      await engineRelease
      if (disposed || reloadSafety.preparing || selectionId !== captureService.selectionId || !isCapturing.value) return
      captureFailure.value = null
      mediaContinuityLost = false
      if (selectionDurationMs >= SLOW_CAPTURE_SELECTION_MS) {
        ElMessage({
          type: 'warning',
          message: String(i18n.global.t('live.captureSelectionSlow')),
          duration: 6_000,
          showClose: true,
        })
      }
      await initializeOcr()
      // Preview can run immediately; starting a record awaits this shared task.
      if (!disposed && selectionId === captureService.selectionId && isCapturing.value) {
        void prepareAutomaticPreset(undefined, 'selection').catch(() => undefined).then(async () => {
          if (!disposed && !reloadSafety.preparing && selectionId === captureService.selectionId
            && isCapturing.value && !isStarting.value && !pipeline && ocrStatus.value !== 'error'
            && (!session.value || session.value.status === 'ended')) {
            await initializeOcr()
          }
        }).catch(() => undefined)
      }
    } catch (caught) {
      if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
      setCaptureStartupPhase(captureService.status === 'selected' ? 'error' : 'idle')
      reportCaptureFailure(caught, 'selection')
    } finally {
      captureSelectionPending.value = false
    }
  }

  function reportCaptureFailure(caught: unknown, phase: CaptureFailurePhase): void {
    const failure = classifyCaptureFailure(caught)
    captureFailure.value = failure
    captureDiagnostic.value = captureService.lastDiagnostic ?? createCaptureDiagnostic(caught, phase, capturePreference.value)
    const messageKey = failure === 'permission-denied' ? 'live.capturePermissionDenied'
      : ['security-restricted', 'invalid-state', 'insecure-context', 'policy-blocked'].includes(failure) ? `captureRecovery.${failure}Hint`
      : failure === 'unsupported' ? 'live.captureUnsupported'
        : failure === 'timeout' ? 'live.captureTimeout' : 'live.captureSourceUnavailable'
    if (failure === 'permission-denied') {
      ElMessage({
        type: 'warning',
        message: String(i18n.global.t(messageKey)),
        duration: 4_500,
        showClose: true,
      })
    }
  }

  function startSession(): Promise<void> {
    if (disposed || reloadSafety.preparing || pendingFinalization.value || isSwitchingOcrModel.value
      || (isLoadingPreset.value && !automaticPresetPreparation)) return Promise.resolve()
    if (startSessionPromise) return startSessionPromise
    if (isEnding.value || (session.value && session.value.status !== 'ended')) return Promise.resolve()
    const requestId = ++startRequestId
    startSessionPromise = (async () => {
      if (!isCapturing.value) {
        await selectWindow()
        if (requestId !== startRequestId || error.value || !isCapturing.value) return
      }
      const selectionId = captureService.selectionId
      isStarting.value = !firstUseCalibrationBlocking.value
      let typeReady = false
      try { typeReady = await prepareAutomaticPreset(undefined, 'session-start') }
      catch (caught) { reportPresetPreparationFailure(caught, selectionId) }
      if (requestId !== startRequestId) return
      if (!typeReady) {
        if (gameTypeChoice.value && selectionId === captureService.selectionId) {
          gameTypeChoice.value = { ...gameTypeChoice.value, entry: 'session-start' }
          gameTypeChoiceOpen.value = true
        }
        return
      }
      if (disposed || selectionId !== captureService.selectionId || !isCapturing.value) return
      // A confirmed type change may have released the old preview pipeline.
      if (!pipeline && ocrStatus.value !== 'error') await initializeOcr()
      if (requestId !== startRequestId) return
      if (firstUseCalibrationBlocking.value) {
        isStarting.value = false
        await new Promise<void>(resolve => {
          const finish = () => { stop(); cancelFirstUseWait = null; resolve() }
          const stop = watch(firstUseCalibrationBlocking, blocked => { if (!blocked) finish() })
          cancelFirstUseWait = finish
        })
        if (firstUseHistory.state === 'pending') return
      }
      if (requestId !== startRequestId || disposed || selectionId !== captureService.selectionId || !isCapturing.value) return
      if (reloadSafety.preparing || pendingFinalization.value) return
      if (!isCapturing.value || !profile.value || !sessionEngine) return
      isStarting.value = true

      startIssue.value = null
      if (ocrStatus.value !== 'ready' || !pipeline) return
      const startGeneration = captureGeneration
      const startProfile = profile.value
      const baseline = await acquireSessionStartBaseline()
      if (!baseline || disposed || reloadSafety.preparing || pendingFinalization.value
        || selectionId !== captureService.selectionId || startGeneration !== captureGeneration
        || startProfile !== profile.value || !isCapturing.value) return
      const startedAt = baseline.capturedAt
      captureGeneration += 1
      session.value = sessionEngine.start(profile.value.id, startedAt, baseline, captureGeneration)
      restrictSessionRankForPerformance()
      restrictSessionRankForSource()
      const initialExpAdmission = new ExpAdmissionFilter()
      initialExpAdmission.reset(baseline)
      sessionExpCheckpoint = initialExpAdmission.checkpoint()
      journey.value = sessionEngine.journey()
      liveRates.value = sessionEngine.liveRates(startedAt)
      startLiveRateRefresh()
      autosave.start()
      await persistSession()
      if (pipeline && ocrStatus.value === 'ready') {
        activePipelineGeneration = captureGeneration
        await pipeline.beginSession(captureService.video, session.value.id, baseline, captureGeneration, startedAt)
      } else if (ocrStatus.value === 'ready') {
        startPipeline(session.value.id, baseline, captureGeneration)
      } else {
        void initializeOcr().catch(() => undefined)
      }
    })().finally(() => {
      isStarting.value = false
      startSessionPromise = null
    })
    return startSessionPromise
  }

  async function pauseSession(): Promise<void> {
    if (reloadSafety.preparing || pendingFinalization.value) return
    if (!sessionEngine || isEnding.value) return
    const pausedAt = Date.now()
    session.value = sessionEngine.pause(pausedAt)
    pipeline?.invalidateExpDelivery()
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(pausedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  async function resumeSession(): Promise<void> {
    if (reloadSafety.preparing || pendingFinalization.value) return
    if (!sessionEngine || isEnding.value) return
    const resumedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.setCaptureGeneration(captureGeneration)
    activePipelineGeneration = pipeline ? captureGeneration : -1
    pipeline?.resetBaseline(undefined, sessionExpCheckpoint ?? undefined)
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  function endSession(): Promise<void> {
    if (endSessionPromise) return endSessionPromise
    if (!pendingFinalization.value && (!sessionEngine || !session.value || session.value.status === 'ended')) return Promise.resolve()
    const engine = sessionEngine
    endSessionPromise = (async () => {
      isEnding.value = true
      resetCaptureRecovery()
      const pendingAutosave = autosave.stop()
      if (!pendingFinalization.value && engine) {
        if (session.value?.status === 'running' && isCapturing.value && pipeline) {
          settlementController = new AbortController()
          let timeoutId = 0
          const timeout = new Promise<boolean>((resolve) => {
            timeoutId = window.setTimeout(() => {
              settlementController?.abort()
              resolve(false)
            }, finalSamplingBudgetMs(captureIntervalMs()))
          })
          try {
            const completed = await Promise.race([
              pipeline.settle(captureService.video, ['level', 'exp'], settlementController.signal),
              timeout,
            ])
            if (!completed) ElMessage.warning(i18n.global.t('performance.finalSampleIncomplete'))
          } catch {
            // Keep all previously confirmed totals if the final sample fails.
            ElMessage.warning(i18n.global.t('performance.finalSampleIncomplete'))
          } finally {
            window.clearTimeout(timeoutId)
            settlementController.abort()
            settlementController = null
          }
        }

        const endedAt = Date.now()
        stopLiveRateRefresh()
        session.value = engine.end(endedAt)
        journey.value = engine.journey()
        liveRates.value = engine.liveRates(endedAt)
        const rankItems = createRankQueueItems(session.value.id, engine.rankSubmissions())
        pendingFinalization.value = [session.value, journey.value, rankItems]
      }
      const finalization = pendingFinalization.value
      if (!finalization) return
      await pendingAutosave
      await saveEndedSessionState(...finalization)
      pendingFinalization.value = null
      if (finalization[2].length) notifyRankQueueChanged()
    })().finally(() => {
      isEnding.value = false
      endSessionPromise = null
    })
    return endSessionPromise
  }

  async function updateProfile(next: CaptureProfile): Promise<void> {
    startBaselineController?.abort('profile-changed')
    const previous = profile.value
    if (previous && calibrationRegionsChanged(previous, next).length) automaticPresetController?.abort('profile-changed')
    const sourceChanged = previous ? captureSourceChanged(previous, next) : true
    const adaptiveMetricToReset = previous ? changedAdaptiveMetric(previous, next) : null
    const onlyRegionsChanged = preset.value?.layoutVersion && previous && previous.sourceWidth === next.sourceWidth && previous.sourceHeight === next.sourceHeight
      && JSON.stringify(previous.colorCalibration) === JSON.stringify(next.colorCalibration)
      && JSON.stringify(previous.recognition) === JSON.stringify(next.recognition)
      && calibrationRegionsChanged(previous, next).length > 0
    profile.value = { ...cloneCaptureProfile(next), recognition: recognitionForTier(next.recognition, appliedPerformanceTier.value), updatedAt: Date.now() }
    pipService.refresh()
    needsCalibration.value = false
    pipeline?.updateProfile(profile.value)
    if (sourceChanged && !onlyRegionsChanged) {
      captureGeneration += 1
      pipeline?.setCaptureGeneration(captureGeneration, adaptiveMetricToReset ?? undefined)
      activePipelineGeneration = pipeline ? captureGeneration : -1
      pipeline?.resetBaseline(adaptiveMetricToReset ?? undefined, session.value?.status !== 'ended' ? sessionExpCheckpoint ?? undefined : undefined)
    }
    const snapshot = profile.value
    await saveBeforeReload(`profile:${snapshot.id}`, async () => { await db.profiles.put(snapshot) })
  }

  function setCalibrationMetric(metric: RecognitionMetricKey | null): void {
    calibrationMetric = metric
    pipeline?.setDiagnosticMetric(metric)
  }

  async function initializeCalibration(options: CalibrationInitializationOptions = {}): Promise<RecognitionMetricKey[]> {
    if (isSwitchingOcrModel.value || firstUseCalibrationBlocking.value) throw new Error('First-use calibration is in progress')
    if (options.action !== 'retry-save') {
      const selectionId = captureService.selectionId
      try {
        if (!await prepareAutomaticPreset(options.signal, 'calibration')) throw new DOMException('Game type selection required', 'AbortError')
      }
      catch (caught) { reportPresetPreparationFailure(caught, selectionId); throw caught }
      await initializeOcr()
    }
    return performCalibration(options)
  }

  function withOcrModel(source: GamePreset, model: OcrRecognitionModel): GamePreset {
    // External/test presets can own their own resource descriptors.
    if (!isDetectedPreset(source.id)) return source
    return { ...source, remoteResources: { ...source.remoteResources, ocrModels: mxdcOcrModels(model) } }
  }

  async function selectOcrModel(value: OcrRecognitionModel): Promise<void> {
    if (disposed || ocrModelSwitchBlocked.value || startSessionPromise || automaticPresetPreparation) return
    const model = normalizeOcrRecognitionModel(value)
    if (model === app.ocrRecognitionModel && ocrStatus.value !== 'error') return
    isSwitchingOcrModel.value = true
    const release = blockReload()
    try {
      captureGeneration += 1
      await releaseRecognitionForCaptureSelection()
      if (disposed || !preset.value) return
      app.ocrRecognitionModel = model
      preset.value = withOcrModel(preset.value, model)
      latestObservation = null
      latestObservationGeneration = -1
      rawReading.value = null
      gameState.value = null
      error.value = null
      await initializeOcr()
    } catch (caught) {
      ocrStatus.value = 'error'
      ocrErrorType.value = 'initialization'
      ocrError.value = caught instanceof Error ? caught.message : String(caught)
    } finally {
      isSwitchingOcrModel.value = false
      release()
    }
  }

  function reportPresetPreparationFailure(caught: unknown, selectionId: number): void {
    if (disposed || reloadSafety.preparing || selectionId !== captureService.selectionId) return
    const reason = caught instanceof GameDetectionPreparationError ? caught.reason : 'probe-error'
    if (silentGameDetectionFailure(reason) || caught instanceof DOMException && caught.name === 'AbortError') return
    error.value = String(i18n.global.t(reason === 'ambiguous' ? 'presets.ambiguous'
      : reason === 'timeout' ? 'calibration.autoDialog.reasons.timeout' : `startup.gameDetectionFailures.${reason}`))

  }

  function newPreparationDiagnostic(entry: CapturePreparationEntry, selectionId: number): CapturePreparationDiagnostic {
    const dimensions = { width: captureService.video.videoWidth, height: captureService.video.videoHeight }
    return { selectionId, entry, startedAt: Date.now(), elapsedMs: 0, outcome: 'preparing', reason: null,
      stages: { pickerExitMs: null, gameDetectionMs: null }, pickerExitFallback: null, sampleCount: 0,
      initialDimensions: dimensions, currentDimensions: dimensions, initialVisibility: document.visibilityState,
      currentVisibility: document.visibilityState, profileIdentityChanged: false }
  }

  function publishPreparationDiagnostic(diagnostic: CapturePreparationDiagnostic): void {
    if (diagnostic.selectionId !== captureService.selectionId) return
    diagnostic.elapsedMs = Date.now() - diagnostic.startedAt
    diagnostic.currentDimensions = { width: captureService.video.videoWidth, height: captureService.video.videoHeight }
    diagnostic.currentVisibility = document.visibilityState
    capturePreparationDiagnostic.value = structuredClone(diagnostic)
    performanceDiagnostics.setCapturePreparation(diagnostic)
  }

  /** An unfinished record keeps its preset through calibration and capture recovery. */
  function prepareAutomaticPreset(signal?: AbortSignal, entry: CapturePreparationEntry = 'calibration'): Promise<boolean> {
    if (session.value && session.value.status !== 'ended') return Promise.resolve(true)
    if (automaticPresetPreparation) return automaticPresetPreparation
    if (!isCapturing.value || !profile.value) return Promise.resolve(false)
    // Saved positions do not establish the game family of a new capture source.
    if (!isDetectedPreset(profile.value.presetId)) return Promise.resolve(true)
    const video = captureService.video, sourceObject = video.srcObject, selectionId = captureService.selectionId
    const context = presetContext()
    if (resolvedPresetContext === context && resolvedPresetSource === sourceObject) return Promise.resolve(true)
    if (gameTypeChoice.value && choiceContext === context && choiceSource === sourceObject) {
      gameTypeChoiceOpen.value = true
      return Promise.resolve(false)
    }
    clearGameTypeChoice()
    if (!video.videoWidth || !video.videoHeight) return Promise.resolve(false)
    const width = video.videoWidth, height = video.videoHeight
    const initialProfile = profile.value
    const identity = captureProfileIdentity(initialProfile)
    const diagnostic = entry === 'selection' && capturePreparationDiagnostic.value?.selectionId === selectionId
      ? structuredClone(capturePreparationDiagnostic.value) : newPreparationDiagnostic(entry, selectionId)
    const detectionStartedAt = Date.now()
    diagnostic.outcome = 'preparing'
    diagnostic.reason = null
    error.value = null
    const controller = new AbortController()
    automaticPresetController = controller
    const cancel = () => controller.abort('user-cancelled')
    signal?.addEventListener('abort', cancel, { once: true })
    if (signal?.aborted) cancel()
    let applyingPreset = false
    let expectedPresetRevision = presetLoadRevision
    const contextFailure = (): GameDetectionFailureReason | null => {
      if (disposed) return 'disposed'
      if (reloadSafety.preparing) return 'reload'
      if (selectionId !== captureService.selectionId || captureService.video !== video || video.srcObject !== sourceObject) return 'source-replaced'
      if (!isCapturing.value || video.ended) return 'capture-ended'
      if (video.videoWidth !== width || video.videoHeight !== height) return 'dimensions-changed'
      if (presetLoadRevision !== expectedPresetRevision) return 'profile-changed'
      if (!applyingPreset && captureProfileIdentity(profile.value) !== identity) return 'profile-changed'
      if (session.value && session.value.status !== 'ended') return 'source-replaced'
      return null
    }
    const isCurrent = () => !controller.signal.aborted && !contextFailure()
    const failureReason = (): GameDetectionFailureReason => controller.signal.aborted
      ? controller.signal.reason as GameDetectionFailureReason : contextFailure() ?? 'probe-error'
    const needsChoice = (reason: NonNullable<typeof gameTypeChoice.value>['reason']) => {
      diagnostic.outcome = 'fallback'
      diagnostic.reason = reason
      gameTypeChoice.value = { reason, entry }
      choiceContext = presetContext()
      choiceSource = sourceObject
      gameTypeChoiceOpen.value = true
      return false
    }
    let preparation!: Promise<boolean>
    preparation = (async () => {
      automaticPresetMonitor.reset()
      const source = new VideoFrameSource(video)
      const deadlineAt = Date.now() + 10_000
      const timer = window.setTimeout(() => controller.abort('timeout'), 10_000)
      const checkDetection = () => {
        if (Date.now() >= deadlineAt) controller.abort('timeout')
        if (!isCurrent()) throw new GameDetectionPreparationError(failureReason())
      }
      let rejectAborted!: () => void
      const aborted = new Promise<never>((_resolve, reject) => {
        rejectAborted = () => { automaticPresetMonitor.reset(); reject(new GameDetectionPreparationError(failureReason())) }
        controller.signal.addEventListener('abort', rejectAborted, { once: true })
      })
      void aborted.catch(() => undefined)
      const invalidateContext = () => { const reason = contextFailure(); if (reason) controller.abort(reason) }
      const stopContextWatch = watch([profile, captureStatus, () => reloadSafety.preparing], invalidateContext, { flush: 'sync' })
      video.addEventListener('resize', invalidateContext)
      captureService.addEventListener('preview', invalidateContext)
      captureService.addEventListener('status', invalidateContext)
      publishPreparationDiagnostic(diagnostic)
      let firstId = -1
      const savedLayout = { hud: initialProfile.hudRect, regions: initialProfile.autoRois ?? {} }
      let reuseSaved = false
      const savedIsValid = () => validatePresetUi(initialProfile.presetId as DetectedPreset, video, width, height, savedLayout)
      try {
        for (let sample = 0; sample < 2; sample++) {
          checkDetection()
          if (sample) {
            if (healthyStaticCapture(video)) await calibrationDelay(500, controller.signal)
            else if (!await source.waitAfter(firstId, controller.signal, {
              request: signal => captureService.requestFrameRefresh(signal), intervalMs: captureIntervalMs,
            })) throw new GameDetectionPreparationError(failureReason())
          }
          checkDetection()
          firstId = source.read()?.id ?? firstId
          diagnostic.sampleCount += 1
          publishPreparationDiagnostic(diagnostic)
          if (sample === 0) reuseSaved = savedIsValid()
          if (reuseSaved && savedIsValid()) {
            if (!sample) continue
            diagnostic.outcome = 'fallback'
            diagnostic.resolution = 'saved-profile'
            rememberPresetContext()
            return true
          }
          reuseSaved = false
          const decision = await Promise.race([
            automaticPresetMonitor.inspect(video, width, height, initialProfile, Date.now(), true), aborted,
          ])
          checkDetection()
          if (decision.reason) diagnostic.reason = decision.reason
          if (decision.kind === 'unknown') return needsChoice(decision.reason ?? 'unknown')
          if (decision.kind === 'ambiguous') return needsChoice('ambiguous')
          if (!decision.confirmed) continue
          window.clearTimeout(timer)
          if (decision.kind !== initialProfile.presetId) {
            applyingPreset = true
            expectedPresetRevision += 1
            await loadPreset(decision.kind, false, isCurrent, aborted)
            if (!isCurrent()) throw new GameDetectionPreparationError(failureReason())
            await Promise.race([loadCalibrationSize(width, height, isCurrent), aborted])
            if (!isCurrent()) throw new GameDetectionPreparationError(failureReason())
          }
          error.value = null
          diagnostic.outcome = 'ready'
          diagnostic.resolution = 'automatic'
          rememberPresetContext()
          return true
        }
        return needsChoice('unconfirmed')
      } catch (caught) {
        const name = typeof caught === 'object' && caught !== null && 'name' in caught ? String(caught.name) : ''
        const reason = caught instanceof GameDetectionPreparationError ? caught.reason
          : !isCurrent() ? failureReason() : name === 'TimeoutError' ? 'timeout' : 'probe-error'
        diagnostic.reason = reason
        if (['timeout', 'probe-error'].includes(reason) && !contextFailure()) return needsChoice(reason as 'timeout' | 'probe-error')
        diagnostic.outcome = silentGameDetectionFailure(reason) ? 'cancelled' : 'error'
        throw new GameDetectionPreparationError(reason)
      } finally {
        window.clearTimeout(timer)
        controller.signal.removeEventListener('abort', rejectAborted)
        stopContextWatch()
        video.removeEventListener('resize', invalidateContext)
        captureService.removeEventListener('preview', invalidateContext)
        captureService.removeEventListener('status', invalidateContext)
        automaticPresetMonitor.reset()
        source.dispose()
        diagnostic.stages.gameDetectionMs = Date.now() - detectionStartedAt
        diagnostic.profileIdentityChanged = diagnostic.reason === 'profile-changed' || captureProfileIdentity(profile.value) !== identity
        if (automaticPresetController === controller) {
          publishPreparationDiagnostic(diagnostic)
        }
      }
    })().finally(() => {
      signal?.removeEventListener('abort', cancel)
      if (automaticPresetController === controller) automaticPresetController = null
      if (automaticPresetPreparation === preparation) automaticPresetPreparation = null
    })
    automaticPresetPreparation = preparation
    return preparation
  }

  function dismissGameTypeChoice(): void {
    gameTypeChoiceOpen.value = false
    if (gameTypeChoice.value) gameTypeChoice.value = { ...gameTypeChoice.value, entry: 'selection' }
  }

  async function retryGameTypeDetection(): Promise<void> {
    if (isChoosingGameType.value || !isCapturing.value || (session.value && session.value.status !== 'ended')) return
    const entry = gameTypeChoice.value?.entry ?? 'selection'
    clearGameTypeChoice()
    resolvedPresetContext = ''
    const selectionId = captureService.selectionId
    try { if (!await prepareAutomaticPreset(undefined, entry)) return }
    catch (caught) { reportPresetPreparationFailure(caught, selectionId); return }
    if (!disposed && selectionId === captureService.selectionId && isCapturing.value) {
      await initializeOcr()
      if (entry === 'session-start') void startSession()
    }
  }

  async function changeCalibrationGameType(id: DetectedPreset): Promise<void> {
    if (id === preset.value?.id && !gameTypeChoice.value && resolvedPresetContext === presetContext()
      && resolvedPresetSource === captureService.video.srcObject) return
    if (gameTypeChoice.value?.reason === 'ambiguous' || automaticPresetPreparation || isLoadingPreset.value || isStarting.value || isEnding.value
      || isInitializingCalibration.value || firstUseCalibrationBlocking.value || pendingFinalization.value
      || reloadSafety.preparing || !isCapturing.value || (session.value && session.value.status !== 'ended')) return
    await applyGameType(id, { reason: 'unconfirmed', entry: 'calibration' })
  }

  async function chooseGameType(id: DetectedPreset): Promise<void> {
    if (gameTypeChoice.value) await applyGameType(id, gameTypeChoice.value)
  }

  async function applyGameType(id: DetectedPreset, choice: NonNullable<typeof gameTypeChoice.value>): Promise<void> {
    if (choice.reason === 'ambiguous' || isChoosingGameType.value || !isDetectedPreset(id)) return
    const video = captureService.video, sourceObject = video.srcObject, selectionId = captureService.selectionId
    const width = video.videoWidth, height = video.videoHeight
    const revision = presetLoadRevision + 1
    const isCurrent = () => !disposed && !reloadSafety.preparing && isCapturing.value
      && presetLoadRevision === revision
      && captureService.selectionId === selectionId && captureService.video === video && video.srcObject === sourceObject
      && video.videoWidth === width && video.videoHeight === height && (!session.value || session.value.status === 'ended')
    isChoosingGameType.value = true
    try {
      await loadPreset(id, false, isCurrent)
      if (!isCurrent()) return
      await loadCalibrationSize(width, height, isCurrent)
      if (!isCurrent()) return
      clearGameTypeChoice()
      rememberPresetContext()
      const diagnostic = capturePreparationDiagnostic.value
      if (diagnostic?.selectionId === selectionId) publishPreparationDiagnostic({ ...diagnostic, outcome: 'ready', resolution: 'manual' })
      await initializeOcr()
      if (isCurrent() && choice.entry === 'session-start') void startSession()
    } catch (caught) {
      if (isCurrent()) {
        gameTypeChoice.value = choice
        choiceContext = presetContext()
        choiceSource = sourceObject
        gameTypeChoiceOpen.value = true
        error.value = caught instanceof Error ? caught.message : String(caught)
      }
    } finally {
      isChoosingGameType.value = false
      if (presetLoadRevision !== revision && gameTypeChoice.value === choice) clearGameTypeChoice()
    }
  }

  async function performCalibration(options: CalibrationInitializationOptions = {}): Promise<RecognitionMetricKey[]> {
    if (!pipeline || !isCapturing.value || isInitializingCalibration.value || isStarting.value || isEnding.value || reloadSafety.preparing) throw new Error('Calibration is unavailable')
    isInitializingCalibration.value = true
    try { return await pipeline.initializeLayout(captureService.video, options) }
    finally { isInitializingCalibration.value = false }
  }

  function invalidateFirstUseCalibration(): void {
    if (!firstUseCalibrationBlocking.value) return
    firstUseOperation += 1
    firstUseController?.abort()
    firstUseController = null
    if (firstUseCalibrationBusy.value) pipeline?.stop()
    firstUseCalibrationOpen.value = false
    firstUseCalibrationPhase.value = 'idle'
    firstUseCalibrationFrame.value = null
  }

  function skipFirstUseCalibration(): void {
    if (firstUseCalibrationProgress.value.stage !== 'saving') firstUseController?.abort('user-skip')
  }

  function closeFirstUseCalibration(): void {
    if (firstUseCalibrationBusy.value) return
    firstUseOperation += 1
    firstUseCalibrationOpen.value = false
    firstUseCalibrationPhase.value = 'idle'
    firstUseCalibrationFrame.value = null
  }

  async function runFirstUseCalibration(metrics: readonly RecognitionMetricKey[] = recognitionMetricKeys, action: CalibrationInitializationOptions['action'] = 'locate'): Promise<void> {
    if (!['waiting', 'result'].includes(firstUseCalibrationPhase.value) || !pipeline || !profile.value || !isCapturing.value || ocrStatus.value !== 'ready' || reloadSafety.preparing) return
    const initial = firstUseCalibrationPhase.value === 'waiting'
    const preparingOperation = firstUseOperation
    firstUseCalibrationPhase.value = 'running'
    firstUseCalibrationOpen.value = true
    if (action !== 'retry-save') {
      const selectionId = captureService.selectionId
      let typeReady = false
      try { typeReady = await prepareAutomaticPreset(undefined, 'first-use-calibration') }
      catch (caught) { reportPresetPreparationFailure(caught, selectionId) }
      if (preparingOperation !== firstUseOperation) return
      if (!typeReady) {
        firstUseCalibrationPhase.value = 'idle'
        firstUseCalibrationOpen.value = false
        return
      }
      await initializeOcr()
      if (!pipeline || !profile.value || !isCapturing.value) return
    }
    if (captureService.video.videoWidth !== profile.value.sourceWidth || captureService.video.videoHeight !== profile.value.sourceHeight) {
      // The frame may resize while the startup dialog is still leaving.
      invalidateFirstUseCalibration()
      pipeline.resume(captureService.video)
      return
    }
    const currentPipeline = pipeline, source = captureService.video.srcObject, selectionId = captureService.selectionId
    const profileId = profile.value.id, width = captureService.video.videoWidth, height = captureService.video.videoHeight, generation = captureGeneration
    const operation = ++firstUseOperation
    const controller = new AbortController()
    firstUseController = controller
    const isCurrent = () => operation === firstUseOperation && pipeline === currentPipeline && isCapturing.value
      && captureService.video.srcObject === source && captureService.selectionId === selectionId && profile.value?.id === profileId
      && captureService.video.videoWidth === width && captureService.video.videoHeight === height && captureGeneration === generation && !reloadSafety.preparing
    const previous = initial ? { located: [], saved: [], failed: [] } : firstUseCalibrationProgress.value
    const keep = (items: readonly RecognitionMetricKey[]) => items.filter(metric => !metrics.includes(metric))
    const merge = (progress: CalibrationProgress): CalibrationProgress => ({ ...progress, metrics,
      located: [...keep(previous.located), ...progress.located], saved: [...keep(previous.saved), ...progress.saved],
      failed: [...keep(previous.failed ?? []), ...(progress.failed ?? [])] })
    firstUseCalibrationPhase.value = 'running'
    firstUseCalibrationOutcome.value = ''
    firstUseCalibrationFrame.value = null
    firstUseCalibrationProgress.value = merge({ stage: 'preparing', located: [], confirming: [], saved: [] })
    firstUseCalibrationRetained.value = recognitionMetricKeys.filter(metric => profile.value?.roiOverrides[metric] || profile.value?.autoRois?.[metric])
    const recognize = async () => {
      if (initial) await firstUseHistory.refresh()
      if (!isCurrent()) return
      if (initial && firstUseHistory.state === 'completed') {
        firstUseCalibrationPhase.value = 'idle'
        currentPipeline.resume(captureService.video)
        return
      }
      firstUseCalibrationOpen.value = true
      try {
        const completed = await performCalibration({ metrics, action, signal: controller.signal, isCurrent,
          onFrame: frame => { if (isCurrent() && !controller.signal.aborted) firstUseCalibrationFrame.value = frame },
          onProgress: progress => { if (isCurrent() && !controller.signal.aborted) firstUseCalibrationProgress.value = merge(progress) },
        })
        if (!isCurrent()) return
        firstUseCalibrationProgress.value = merge({ ...firstUseCalibrationProgress.value, stage: 'ended', located: completed, confirming: [], saved: completed, failed: metrics.filter(metric => !completed.includes(metric)) })
        firstUseCalibrationOutcome.value = recognitionMetricKeys.every(metric => metric === 'mesos' || firstUseCalibrationProgress.value.saved.includes(metric))
          ? 'complete' : 'partial'
      } catch {
        if (!isCurrent()) return
        firstUseCalibrationOutcome.value = controller.signal.reason === 'user-skip' ? 'cancelled'
          : firstUseCalibrationProgress.value.stage === 'saving' ? 'saveFailed' : 'failed'
      }
      if (!isCurrent()) return
      await firstUseHistory.complete(isCurrent)
      if (!isCurrent()) return
      firstUseCalibrationProgress.value = { ...firstUseCalibrationProgress.value, stage: 'ended' }
      firstUseCalibrationPhase.value = 'result'
    }
    try {
      if (initial) await withInitialLayoutLock(recognize, controller.signal)
      else await recognize()
    } finally {
      if (operation === firstUseOperation) {
        firstUseController = null
        if (!isCurrent()) {
          // A resize can invalidate a task without changing reactive capture status.
          invalidateFirstUseCalibration()
          if (pipeline === currentPipeline && isCapturing.value && !reloadSafety.preparing) currentPipeline.resume(captureService.video)
        }
      }
    }
  }

  async function loadCalibrationSize(width: number, height: number, isCurrent: () => boolean = () => true): Promise<CaptureProfile> {
    const current = profile.value
    const gamePreset = preset.value
    if (!current || !gamePreset) throw new Error('No capture profile')
    if (current.sourceWidth === width && current.sourceHeight === height) return current
    const profiles = await db.profiles.where('[presetId+presetVersion]').equals([current.presetId, current.presetVersion]).toArray()
    if (!isCurrent()) throw new DOMException('Calibration cancelled', 'AbortError')
    const matching = profiles.filter(item => item.layoutVersion === gamePreset.layoutVersion && item.sourceWidth === width && item.sourceHeight === height)
      .sort((a, b) => b.updatedAt - a.updatedAt)[0]
    const next = matching ? { ...matching, recognition: current.recognition } : {
      ...cloneCaptureProfile(current), id: crypto.randomUUID(), sourceWidth: width, sourceHeight: height,
      attributeSources: undefined, autoRois: {}, roiOverrides: {}, colorCalibration: {}, hudRect: gamePreset.hud.fallback, updatedAt: Date.now(),
    }
    await db.profiles.put(next)
    if (!isCurrent()) throw new DOMException('Calibration cancelled', 'AbortError')
    if (profile.value !== current) throw new Error('Capture profile changed')
    profile.value = next
    return next
  }

  async function saveCalibrationProfile(next: CaptureProfile, previous: CaptureProfile, isCurrent?: () => boolean): Promise<CaptureProfile> {
    if (isCurrent && !isCurrent()) throw new Error('Calibration cancelled')
    const current = profile.value
    if (!current || current.id !== previous.id || current.updatedAt !== previous.updatedAt) throw new Error('Capture profile changed')
    const savingPipeline = pipeline
    const saved = cloneCaptureProfile(next)
    const canSave = () => profile.value === current && pipeline === savingPipeline && (!isCurrent || isCurrent())
    await saveBeforeReload(`profile:${saved.id}`, async () => {
      // A stale queued save must also become a no-op when retried before reload.
      if (!canSave()) return
      if (isCurrent) {
        await db.transaction('rw', db.profiles, async () => {
          if (!canSave()) throw new Error('Capture profile changed')
          await db.profiles.put(saved)
          if (!canSave()) throw new Error('Capture profile changed')
        })
      } else await db.profiles.put(saved)
    })
    if (profile.value !== current || pipeline !== savingPipeline || isCurrent && !isCurrent()) throw new Error('Capture profile changed')
    if (calibrationRegionsChanged(current, saved).length) automaticPresetController?.abort('profile-changed')
    profile.value = saved
    needsCalibration.value = false
    pipService.refresh()
    return saved
  }

  const pipData = computed<PipData>(() => ({
    locale: app.locale,
    rateUnit: app.rateUnit,
    levelEtaMode: app.levelEtaMode,
    mapRecognitionState: rawReading.value?.diagnostics.metrics?.mapName?.mapConfirmation?.state,
    skin: app.pipSkin,
    theme: app.resolvedTheme,
    streamOptions: app.pipStream,
    levelUpId: latestPipLevelUp.value,
    session: session.value,
    state: gameState.value,
    liveRates: liveRates.value,
    canStart: !isSwitchingOcrModel.value && !firstUseCalibrationBlocking.value && isCapturing.value && !isStarting.value && !isPreparingForReload.value && !hasPendingFinalization.value,
    isStarting: isStarting.value,
    isEnding: isEnding.value,
    isPreparingForReload: isPreparingForReload.value || hasPendingFinalization.value,
    enabledMetrics: {
      mesos: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mesos') : true,
      hp: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'hp') : true,
      mp: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mp') : true,
      mapName: profile.value ? metricRecognitionEnabled(profile.value.recognition, 'mapName') : true,
    },
  }))

  watch(pipData, () => pipService.requestRefresh(), { flush: 'post' })

  const pipDisabledReason = computed(() => isPreparingForReload.value ? 'reloading'
    : isEnding.value || hasPendingFinalization.value ? 'ending' : null)

  function pipPrerequisite(source: 'live' | 'stream'): PipIssueCode | null {
    if (pipDisabledReason.value) return pipDisabledReason.value
    if (source === 'stream') return null
    if (!isCapturing.value && !['selected', 'connecting'].includes(captureStatus.value)) return 'not-connected'
    const overlay = getBuffOverlay()
    if (isCapturing.value && enabledBuffCount(overlay)) return null
    if (ocrStatus.value !== 'ready') return ocrStatus.value === 'error' ? 'ocr-error' : 'ocr-preparing'
    return isCapturing.value ? null : 'not-connected'
  }

  function refreshPipAvailability(): void {
    pipAvailability.value = getPipAvailability(undefined, app.pipWindowMode)
  }

  function dismissPipIssue(): void { pipIssue.value = null }

  watch([captureStatus, ocrStatus, pipDisabledReason], () => {
    if (pipIssue.value?.stage !== 'prerequisite') return
    const code = pipPrerequisite(pipSource)
    pipIssue.value = code ? createPipIssue(new PipOpenError(code, 'prerequisite'), pipAvailability.value) : null
  })

  function openPip(source: 'live' | 'stream' = 'stream', preferredMode: PipWidgetMode = app.pipWindowMode): Promise<PipOpenResult> {
    if (disposed) return Promise.resolve({ status: 'cancelled' })
    if (pipOpenPromise) return pipOpenPromise
    pipSource = source
    refreshPipAvailability()
    const availability = getPipAvailability(undefined, preferredMode)
    const code = availability.reason ?? pipPrerequisite(source)
    if (code) {
      const issue = createPipIssue(new PipOpenError(code, availability.reason ? 'availability' : 'prerequisite'), availability)
      pipIssue.value = issue
      return Promise.resolve({ status: 'failed', issue })
    }
    pipIssue.value = null
    isOpeningPip.value = true
    const open = async (): Promise<PipOpenResult> => {
      try {
        const result = await pipService.open(() => pipData.value, {
          start: startSession, pause: pauseSession, resume: resumeSession, end: endSession,
          heartbeat: handlePipHeartbeat,
        }, app.locale, preferredMode)
        if (result === 'opened' && availability.mode) app.pipWindowMode = availability.mode
        return { status: result === 'cancelled' ? 'cancelled' : 'opened' }
      } catch (error) {
        refreshPipAvailability()
        const issue = createPipIssue(classifyPipError(error, 'initialization'), availability)
        pipIssue.value = issue
        return { status: 'failed', issue }
      } finally {
        isOpeningPip.value = false
      }
    }
    // Call immediately: deferring open to a .then() would lose user activation.
    const pending = open()
    pipOpenPromise = pending
    void pending.then(() => { if (pipOpenPromise === pending) pipOpenPromise = null })
    return pending
  }

  async function initializeOcr(): Promise<void> {
    if (disposed) return
    const lifecycleRevision = ocrLifecycleRevision
    const selectionId = captureService.selectionId
    const hasSelectedSource = captureStatus.value === 'selected' || isCapturing.value
    if (!hasSelectedSource) {
      setOcrIdle()
      return
    }
    setCaptureStartupPhase('resources')
    await prepareOcrResources()
    if (disposed || lifecycleRevision !== ocrLifecycleRevision || selectionId !== captureService.selectionId) return
    if ((captureStatus.value !== 'selected' && !isCapturing.value) || ocrStatus.value === 'error') {
      if (ocrStatus.value === 'error') setCaptureStartupPhase('error')
      return
    }
    setCaptureStartupPhase('worker')
    if (captureStatus.value === 'selected') await yieldToMainThread()
    if (disposed || lifecycleRevision !== ocrLifecycleRevision || selectionId !== captureService.selectionId) return
    await initializeOcrEngine()
    if (disposed || lifecycleRevision !== ocrLifecycleRevision || selectionId !== captureService.selectionId) return
    if (ocrStatus.value !== 'ready') {
      if (ocrError.value) setCaptureStartupPhase('error')
      return
    }
    if (captureStatus.value === 'selected') {
      setCaptureStartupPhase('video')
      let info: CaptureInfo
      try {
        info = await captureService.connectSelectedWindow()
      } catch (caught) {
        if (selectionId !== captureService.selectionId || caught instanceof CaptureSelectionReplacedError) return
        setCaptureStartupPhase('error')
        // Capture recovery lives on the page, outside the blocking OCR dialog.
        setOcrIdle()
        reportCaptureFailure(caught, 'track')
        return
      }
      await applyCaptureInfo(info, selectionId)
      if (disposed || lifecycleRevision !== ocrLifecycleRevision || selectionId !== captureService.selectionId) return
    }
    if (disposed || lifecycleRevision !== ocrLifecycleRevision || selectionId !== captureService.selectionId) return
    setCaptureStartupPhase('ready')
    await activateCaptureRecognition()
  }

  async function applyCaptureInfo(info: CaptureInfo, selectionId: number): Promise<void> {
    if (selectionId !== captureService.selectionId) return
    captureLabel.value = info.label
    if (!profile.value) return
    if (preset.value?.locateLayout) await loadCalibrationSize(info.width || profile.value.sourceWidth, info.height || profile.value.sourceHeight)
    else profile.value = {
      ...profile.value,
      sourceWidth: info.width || profile.value.sourceWidth,
      sourceHeight: info.height || profile.value.sourceHeight,
      updatedAt: Date.now(),
    }
    try {
      await db.profiles.put(profile.value)
    } catch (caught) {
      // A local profile save failure does not invalidate a working capture.
      if (selectionId !== captureService.selectionId) return
      error.value = caught instanceof Error ? caught.message : String(caught)
    }
  }

  async function prepareOcrResources(): Promise<void> {
    if (disposed || !preset.value || !profile.value) return
    if (ocrResourcePreparation) return ocrResourcePreparation
    const targetPreset = preset.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint === fingerprint && retainedOcrPreparation) {
      if (ocrStatus.value !== 'initializing') {
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = Math.max(80, ocrProgress.value)
      }
      return
    }

    const lifecycleRevision = ocrLifecycleRevision
    const runtimeBlock = ocrRuntimeBlock()
    if (runtimeBlock) {
      ocrStatus.value = 'error'
      ocrStage.value = 'checking'
      ocrProgress.value = 0
      ocrError.value = runtimeBlock
      ocrErrorType.value = runtimeBlock
      return
    }
    let preparationTask!: Promise<void>
    preparationTask = (async () => {
      releasePreparedOcrResources()
      ocrStatus.value = 'checking'
      ocrStartupDiagnostic.value = null
      ocrStage.value = 'checking'
      ocrProgress.value = 4
      ocrCachedModels.value = 0
      ocrError.value = null
      ocrErrorType.value = null
      ocrCacheDegraded.value = false
      const cached = new Set<string>()
      let pendingProgress: OcrCacheProgress | null = null
      let progressFrame = 0
      const cancelProgress = () => {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        progressFrame = 0
        pendingProgress = null
      }
      cancelOcrProgress = cancelProgress
      const applyProgress = () => {
        progressFrame = 0
        const progress = pendingProgress
        pendingProgress = null
        if (!progress || lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'downloading'
        ocrStage.value = progress.model
        ocrProgress.value = Math.max(5, Math.round(5 + progress.progress * 70))
        ocrCachedModels.value = cached.size
      }
      try {
        const preparation = await ensureOcrModelsCached(targetPreset.remoteResources.ocrModels, (progress) => {
          if (disposed || lifecycleRevision !== ocrLifecycleRevision) return
          if (progress.fromCache) cached.add(progress.model)
          pendingProgress = progress
          if (!progressFrame) progressFrame = requestAnimationFrame(applyProgress)
        })
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) {
          if (progressFrame) cancelAnimationFrame(progressFrame)
          pendingProgress = null
          preparation.releaseTemporaryResources()
          return
        }
        retainedOcrPreparation = preparation
        preparedOcrFingerprint = fingerprint
        ocrCacheDegraded.value = preparation.cacheDegraded
        if (progressFrame) cancelAnimationFrame(progressFrame)
        applyProgress()
        ocrStatus.value = 'prepared'
        ocrStage.value = 'prepared'
        ocrProgress.value = 80
      } catch (caught) {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        pendingProgress = null
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        preparedOcrFingerprint = ''
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = isOcrResourceLoadError(caught) ? 'resource' : 'unknown'
      } finally {
        cancelProgress()
        if (cancelOcrProgress === cancelProgress) cancelOcrProgress = null
      }
    })().finally(() => {
      if (ocrResourcePreparation === preparationTask) ocrResourcePreparation = null
    })
    ocrResourcePreparation = preparationTask
    return preparationTask
  }

  async function initializeOcrEngine(): Promise<void> {
    if (disposed || !preset.value || !profile.value) return
    if (ocrEngineInitialization) return ocrEngineInitialization
    const targetPreset = preset.value
    const targetProfile = profile.value
    const fingerprint = ocrFingerprint(targetPreset)
    if (readyOcrFingerprint === fingerprint) {
      ocrStatus.value = 'ready'
      ocrStage.value = 'ready'
      ocrProgress.value = 100
      return
    }
    if (preparedOcrFingerprint !== fingerprint) return

    const lifecycleRevision = ocrLifecycleRevision
    let initializationTask!: Promise<void>
    initializationTask = (async () => {
      let recognizer: PresetRecognizer | null = null
      ocrStatus.value = 'initializing'
      ocrStage.value = 'initializing'
      ocrError.value = null
      ocrErrorType.value = null
      ocrStartupDiagnostic.value = null
      try {
        recognizer = targetPreset.createRecognizer(targetProfile)
        await recognizer.warmup()
        if (lifecycleRevision !== ocrLifecycleRevision
          || preset.value !== targetPreset
          || ocrFingerprint(targetPreset) !== fingerprint) return
        readyOcrFingerprint = fingerprint
        ocrStage.value = 'ready'
        ocrStatus.value = 'ready'
        ocrProgress.value = 100
        if (retainedOcrPreparation?.usedTemporaryResources && !cacheDegradedWarningShown) {
          cacheDegradedWarningShown = true
          ElMessage({
            type: 'warning',
            message: String(i18n.global.t('startup.cacheDegradedWarning')),
            duration: 5_000,
            showClose: true,
          })
        }
        releasePreparedOcrResources()
      } catch (caught) {
        if (lifecycleRevision !== ocrLifecycleRevision || preset.value !== targetPreset) return
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
        ocrErrorType.value = 'initialization'
        ocrStartupDiagnostic.value = caught instanceof OcrInitializationError ? caught.diagnostic : null
      } finally {
        recognizer?.dispose()
      }
    })().finally(() => {
      if (ocrEngineInitialization === initializationTask) ocrEngineInitialization = null
    })
    ocrEngineInitialization = initializationTask
    return initializationTask
  }

  async function activateCaptureRecognition(): Promise<void> {
    if (!isCapturing.value
      || ocrStatus.value !== 'ready'
      || !preset.value
      || !profile.value
      || activePipelineGeneration === captureGeneration) return

    const firstUse = firstUseHistory.state === 'pending' && Boolean(preset.value.locateLayout)
      && (!session.value || session.value.status === 'ended')
    startPipeline(session.value?.id ?? 'preview', null, captureGeneration, firstUse)
    if (firstUse) firstUseCalibrationPhase.value = 'waiting'
    if (session.value?.status !== 'interrupted' || !sessionEngine) return

    const resumedAt = Date.now()
    sessionEngine.resume(resumedAt, captureGeneration)
    journey.value = sessionEngine.journey()
    pipeline?.resetBaseline(undefined, sessionExpCheckpoint ?? undefined)
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  function releasePreparedOcrResources(): void {
    retainedOcrPreparation?.releaseTemporaryResources()
    retainedOcrPreparation = null
  }

  function releaseRecognitionForCaptureSelection(): Promise<void> {
    resetCaptureRecovery()
    const currentPreset = preset.value
    const currentPipeline = pipeline
    ocrLifecycleRevision += 1
    ocrResourcePreparation = null
    ocrEngineInitialization = null
    currentPipeline?.stop()
    const engineRelease = currentPreset
      ? releaseSharedOcrEngine(currentPreset.remoteResources.ocrModels)
      : Promise.resolve()
    currentPipeline?.dispose()
    pipeline = null
    activePipelineGeneration = -1
    releasePreparedOcrResources()
    preparedOcrFingerprint = ''
    readyOcrFingerprint = ''
    setOcrIdle()
    return engineRelease
  }

  function setOcrIdle(): void {
    ocrStartupDiagnostic.value = null
    ocrStatus.value = 'idle'
    ocrStage.value = 'idle'
    ocrProgress.value = 0
    ocrCachedModels.value = 0
    ocrError.value = null
    ocrErrorType.value = null
    ocrCacheDegraded.value = false
  }

  async function interruptCapture(): Promise<void> {
    resetCaptureRecovery()
    automaticPresetController?.abort('capture-ended')
    resolvedPresetContext = ''
    clearGameTypeChoice()
    markCaptureUnavailable()
    pipeline?.stop()
    activePipelineGeneration = -1
    if (isEnding.value) {
      settlementController?.abort()
      return
    }
    if (!sessionEngine || session.value?.status !== 'running') return
    stopLiveRateRefresh()
    const interruptedAt = Date.now()
    session.value = sessionEngine.interrupt(interruptedAt)
    journey.value = sessionEngine.journey()
    liveRates.value = sessionEngine.liveRates(interruptedAt)
    startLiveRateRefresh()
    await persistSession()
  }

  async function persistSession(): Promise<void> {
    await autosave.flush().catch(() => undefined)
  }

  function startPipeline(sessionId: string, baseline: GameStateSnapshot | null = null, generation = captureGeneration, deferSampling = false): void {
    if (disposed || !preset.value || !profile.value) return
    pipeline?.dispose()
    let nextPipeline!: AnalysisPipeline
    nextPipeline = markRaw(new AnalysisPipeline(
      preset.value,
      profile.value,
      result => {
        if (pipeline !== nextPipeline) {
          performanceDiagnostics.pipelineDelivery('old-pipeline', result.state.capturedAt, result.captureGeneration)
          return
        }
        onPipelineResult(result)
      },
      health => recoverPipeline(nextPipeline, 'scheduler-stall', health),
      performanceConfigs[appliedPerformanceTier.value].processingFpsLimit,
      captureIntervalMs,
      { save: saveCalibrationProfile, loadSize: loadCalibrationSize },
      (frame, engine) => {
        if (pipeline !== nextPipeline || isStarting.value || isEnding.value || isInitializingCalibration.value
          || firstUseCalibrationBlocking.value || !isCapturing.value) return
        captureService.rankCheck.inspect(frame, frame.videoWidth, frame.videoHeight, engine)
      },
      signal => captureService.requestFrameRefresh(signal),
    ))
    pipeline = nextPipeline
    performanceDiagnostics.recordingEvent({ kind: 'pipeline', reason: 'started', sessionId, generation })
    performanceDiagnostics.setSamplingReader(() => pipeline?.health() ?? null)
    activePipelineGeneration = generation
    nextPipeline.setDiagnosticMetric(calibrationMetric)
    nextPipeline.start(captureService.video, sessionId, baseline, generation,
      baseline === null && session.value && session.value.status !== 'ended' ? sessionExpCheckpoint ?? undefined : undefined, { deferSampling })
  }

  function startLiveRateRefresh(): void {
    if (disposed) return
    stopLiveRateRefresh()
    lastLiveRateRefreshAt = 0
    liveRateRefreshTimer = window.setInterval(() => {
      refreshLiveRates()
    }, 1_000)
  }

  function stopLiveRateRefresh(): void {
    if (!liveRateRefreshTimer) return
    window.clearInterval(liveRateRefreshTimer)
    liveRateRefreshTimer = 0
  }

  function refreshLiveRates(now = Date.now()): void {
    observeCaptureRecovery(now)
    if (!sessionEngine || !session.value || session.value.status === 'ended') return
    if (now - lastLiveRateRefreshAt < 1_000) return
    lastLiveRateRefreshAt = now
    if (session.value.status === 'running') session.value = sessionEngine.current(now)
    liveRates.value = sessionEngine.liveRates(now)
    confirmRecoveredExp()
  }

  function handlePipHeartbeat(): void {
    if (isSelectingWindow.value || ocrStatus.value !== 'ready') return
    pipeline?.pulse()
    refreshLiveRates()
  }

  function resetCaptureRecovery(): void {
    if (mediaWaitingTimer !== null) { clearTimeout(mediaWaitingTimer); mediaWaitingTimer = null }
    if (resumeObservationTimer !== null) { clearTimeout(resumeObservationTimer); resumeObservationTimer = null }
    resumeObservationUntil = 0
    captureRecoveryController?.abort()
    captureRecoveryController = null
    recoveryEpisode = false
    recoveryHealthySince = null
    recoveryLastResultAt = null
    recoveryLastFrameId = null
    recoveryConfirmationSince = null
    recoveryConfirmingAt = null
    captureConfirmationSlow.value = false
    captureRecoveryState.value = 'idle'
  }

  function retryCaptureRecovery(): void {
    if (disposed || isEnding.value || isStarting.value || isSelectingWindow.value || reloadSafety.preparing
      || pendingFinalization.value || !isCapturing.value || !pipeline) return
    if (captureRecoveryState.value === 'confirming' || captureRecoveryState.value === 'recognition-stalled'
      || pipeline.health()?.stallReason === 'processing-no-progress') {
      if (captureRecoveryState.value !== 'confirming') captureRecoveryState.value = 'recognition-stalled'
      pipeline.requestExpConfirmation()
      pipeline.pulse(true)
      performanceDiagnostics.captureRecovery({ reason: 'manual', action: 'retry-recognition', generation: captureGeneration, health: pipeline.health() })
      return
    }
    recoverPipeline(pipeline, 'manual')
  }

  function recoverPipeline(expectedPipeline: AnalysisPipeline | null = pipeline,
    reason: import('@/services/performance-diagnostics').CaptureRecoveryEvent['reason'] = 'media-interruption',
    health = expectedPipeline?.health() ?? null): void {
    if (firstUseCalibrationBusy.value || captureRecoveryController || !expectedPipeline
      || pipeline !== expectedPipeline || !isCapturing.value || !preset.value || !profile.value
      || isStarting.value || isEnding.value || disposed) return
    if (reason !== 'manual' && Date.now() < resumeObservationUntil) return
    if (health?.stallReason === 'processing-no-progress' && reason !== 'manual') {
      if (captureRecoveryState.value === 'idle') captureRecoveryState.value = 'recognition-stalled'
      performanceDiagnostics.captureRecovery({ reason, action: 'recognition-stalled', generation: captureGeneration, health })
      return
    }
    recoveryHealthySince = null
    if (recoveryEpisode && reason !== 'manual') {
      if (captureRecoveryState.value !== 'needs-user-action') {
        recoveryConfirmationSince = Date.now()
        recoveryConfirmingAt = null
        captureConfirmationSlow.value = false
        recoveryLastResultAt = null
        recoveryLastFrameId = null
        performanceDiagnostics.captureRecovery({ reason, action: 'stalled-again', generation: captureGeneration, health })
      }
      captureRecoveryState.value = 'needs-user-action'
      return
    }
    const controller = new AbortController()
    captureRecoveryController = controller
    captureRecoveryState.value = 'recovering'
    recoveryLastResultAt = null
    recoveryConfirmationSince = Date.now()
    recoveryConfirmingAt = null
    recoveryLastFrameId = null
    captureConfirmationSlow.value = false
    const selection = captureService.selectionId
    const current = () => !controller.signal.aborted && !disposed && pipeline === expectedPipeline
      && selection === captureService.selectionId && isCapturing.value && !isEnding.value
    performanceDiagnostics.captureRecovery({ reason, action: 'detected', generation: captureGeneration, health })
    if (!recoveryEpisode) {
      recoveryEpisode = true
      captureGeneration += 1
      if (session.value?.status === 'running' && sessionEngine) {
        const now = Date.now()
        session.value = sessionEngine.reconnectCapture(now, captureGeneration)
        liveRates.value = sessionEngine.liveRates(now)
      }
      expectedPipeline.setCaptureGeneration(captureGeneration)
      activePipelineGeneration = captureGeneration
    }
    expectedPipeline.requestExpConfirmation?.()
    const generation = captureGeneration
    mediaContinuityLost = false
    markCaptureUnavailable()
    performanceDiagnostics.captureRecovery({ reason, action: 'rebind-playback', generation: captureGeneration, health })
    void captureService.recoverPlayback(controller.signal).then(result => {
      if (!current()) return
      if (result === 'recovered') markFramesResumed(reason)
      else {
        captureRecoveryState.value = 'needs-user-action'
        performanceDiagnostics.captureRecovery({ reason,
          action: result === 'cancelled' ? 'cancelled' : 'needs-user-action',
          generation, outcome: result, health: expectedPipeline.health() })
      }
    }, () => {
      if (!current()) return
      captureRecoveryState.value = 'needs-user-action'
      performanceDiagnostics.captureRecovery({ reason, action: 'needs-user-action', generation, outcome: 'unavailable' })
    }).finally(() => {
      if (controller.signal.aborted) performanceDiagnostics.captureRecovery({ reason, action: 'cancelled', generation, outcome: 'cancelled' })
      if (captureRecoveryController === controller) captureRecoveryController = null
    })
  }

  function observeCaptureRecovery(now: number): void {
    if (!recoveryEpisode) return
    const health = pipeline?.health()
    const sourceAt = health?.lastSourceFrameAt
    if (sourceAt == null || sourceAt <= (recoveryConfirmationSince ?? -Infinity)
      || now - sourceAt >= sourceStallTimeoutMs()
      || health?.sourceFrameId != null && health.sourceFrameId === recoveryLastFrameId
      || recoveryLastResultAt !== null && sourceAt <= recoveryLastResultAt) return
    recoveryLastFrameId = health?.sourceFrameId ?? null
    now = sourceAt
    const healthyGap = Math.max(2000, 2 * Math.max(captureIntervalMs(), pipeline?.samplingIntervalMs?.() ?? 1000))
    if (recoveryLastResultAt === null || now - recoveryLastResultAt >= healthyGap) recoveryHealthySince = now
    recoveryHealthySince ??= now
    const previous = recoveryLastResultAt
    recoveryLastResultAt = now
    if (!captureRecoveryController && previous !== null && now - previous < healthyGap) markFramesResumed('scheduler-stall')
    if (now - recoveryHealthySince >= 30_000) {
      recoveryEpisode = false
      recoveryHealthySince = null
      recoveryLastResultAt = null
      recoveryLastFrameId = null
    }
  }

  function needsRecoveryExpConfirmation(): boolean {
    return session.value?.status === 'running' && Boolean(profile.value && metricRecognitionEnabled(profile.value.recognition, 'exp'))
  }

  function markFramesResumed(reason: import('@/services/performance-diagnostics').CaptureRecoveryEvent['reason']): void {
    if (captureRecoveryState.value === 'idle' || captureRecoveryState.value === 'confirming') return
    captureRecoveryState.value = needsRecoveryExpConfirmation() ? 'confirming' : 'idle'
    recoveryConfirmingAt = captureRecoveryState.value === 'confirming' ? Date.now() : null
    performanceDiagnostics.captureRecovery({ reason, action: 'frames-resumed', generation: captureGeneration, health: pipeline?.health() })
    confirmRecoveredExp()
  }

  function confirmRecoveredExp(): void {
    if (captureRecoveryState.value !== 'confirming') return
    const required = needsRecoveryExpConfirmation()
    const readout = liveRates.value?.expReadout
    const confirmation = readout?.confirmation
    if (required && (confirmation?.status !== 'ready' || confirmation.sourceAt === null
      || confirmation.sourceAt <= (recoveryConfirmationSince ?? Infinity)
      || Date.now() - confirmation.sourceAt > (readout?.holdMs ?? 15_000))) {
      const intervals = profile.value?.recognition.metricRecognitionIntervalsMs
      const timeout = Math.max(30_000, 3 * Math.max(intervals?.exp ?? 1000, intervals?.level ?? 4000, captureIntervalMs()))
      const slow = recoveryConfirmingAt !== null && Date.now() - recoveryConfirmingAt >= timeout
      if (slow && !captureConfirmationSlow.value) performanceDiagnostics.captureRecovery({ reason: 'scheduler-stall', action: 'confirmation-slow', generation: captureGeneration })
      captureConfirmationSlow.value = slow
      return
    }
    captureRecoveryState.value = 'idle'
    recoveryConfirmationSince = null
    recoveryConfirmingAt = null
    captureConfirmationSlow.value = false
    if (required) performanceDiagnostics.captureRecovery({ reason: 'scheduler-stall', action: 'exp-confirmed', generation: captureGeneration })
  }

  function markCaptureUnavailable(): void {
    const raw = rawReading.value
    const exp = raw?.diagnostics.metrics?.exp
    if (raw && exp) rawReading.value = { ...raw, diagnostics: { ...raw.diagnostics,
      metrics: { ...raw.diagnostics.metrics, exp: { ...exp, failureReason: 'capture-unavailable' } } } }
  }

  function handleCaptureMediaState(state: CaptureMediaState): void {
    if (mediaWaitingTimer !== null) { clearTimeout(mediaWaitingTimer); mediaWaitingTimer = null }
    if (captureRecoveryController) return
    if (state === 'waiting' || state === 'stalled') {
      mediaWaitingTimer = setTimeout(() => {
        mediaWaitingTimer = null
        if (!isCapturing.value) return
        pipeline?.pulse(true)
        if (!sourceHasStalled()) return
        startBaselineController?.abort('capture-unavailable')
        markCaptureUnavailable()
        mediaContinuityLost = true
        invalidateFirstUseCalibration()
        recoverPipeline()
      }, sourceStallTimeoutMs())
      return
    }
    if (state === 'paused' || state === 'muted' || state === 'idle') {
      startBaselineController?.abort('capture-unavailable')
      markCaptureUnavailable()
      mediaContinuityLost = true
      invalidateFirstUseCalibration()
      return
    }
    if (state !== 'playing' && state !== 'unmuted') return

    const shouldRebaseline = mediaContinuityLost
    mediaContinuityLost = false
    pipeline?.pulse(true)
    if (shouldRebaseline && sourceHasStalled()) recoverPipeline()
  }

  function handleVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      void persistSession()
      return
    }
    handlePageResume()
  }

  function handlePageHide(): void {
    startBaselineController?.abort('disposed')
    void persistSession()
  }

  function handlePageFreeze(): void {
    startBaselineController?.abort('capture-unavailable')
    if (session.value && session.value.status !== 'ended') {
      void persistSession().catch(() => undefined)
    }
  }

  function captureIntervalMs(): number { return captureService.frameIntervalMs ?? 1_000 }
  function sourceStallTimeoutMs(): number {
    return pipeline?.health()?.stallTimeoutMs ?? frameStallTimeoutMs(captureIntervalMs(), pipeline?.samplingIntervalMs?.() ?? 1000)
  }
  function sourceHasStalled(): boolean {
    return pipeline?.health()?.stallReason === 'source-no-progress'
  }
  function recognitionPerformanceStatus() { return pipeline?.performanceState?.() ?? null }

  function handlePageResume(): void {
    if (firstUseCalibrationBusy.value) return
    if (!isCapturing.value || !pipeline) {
      return
    }
    if (resumeObservationTimer !== null) return
    const expected = pipeline, selection = captureService.selectionId
    const delay = Math.max(2000, 2 * captureIntervalMs())
    resumeObservationUntil = Date.now() + delay
    // Read current source evidence before treating a background scheduling gap as a lost capture.
    pipeline.pulse(true)
    resumeObservationTimer = setTimeout(() => {
      resumeObservationTimer = null
      resumeObservationUntil = 0
      if (disposed || pipeline !== expected || selection !== captureService.selectionId || !isCapturing.value) return
      expected.pulse(true)
      if (sourceHasStalled()) recoverPipeline(expected, 'page-resume')
    }, delay)
  }

  function restrictSessionRankForPerformance(): void {
    if (!sessionEngine || !session.value || session.value.status === 'ended') return
    session.value = sessionEngine.restrictRankForPerformance(appliedPerformanceTier.value, captureFpsApplied.value) ?? session.value
  }

  function restrictSessionRankForSource(): void {
    const assessment = captureService.rankCheck.assessment
    if (!assessment || assessment.selectionId !== captureService.selectionId || captureService.status !== 'active'
      || !sessionEngine || !session.value || session.value.status === 'ended' || pendingFinalization.value) return
    session.value = sessionEngine.restrictRankForSource(rankSourceRestriction(assessment, session.value.presetId)) ?? session.value
  }

  function onPipelineResult(result: PipelineResult): void {
    if (disposed || result.captureGeneration !== captureGeneration) {
      performanceDiagnostics.pipelineDelivery(disposed ? 'disposed' : 'old-generation', result.state.capturedAt, result.captureGeneration)
      return
    }
    if (captureRecoveryState.value === 'recognition-stalled') captureRecoveryState.value = 'idle'
    observeCaptureRecovery(Date.now())
    latestObservation = result.observation
    latestObservationGeneration = result.captureGeneration
    gameState.value = result.state
    hudLocation.value = result.hud
    if (session.value?.status !== 'running' || !sessionEngine) {
      performanceDiagnostics.pipelineDelivery('not-recording', result.state.capturedAt, result.captureGeneration)
      const progress = expRecognitionProgress.observe(`${captureGeneration}:${session.value?.id ?? 'preview'}`, result.observation)
      const d = result.raw.diagnostics.metrics?.exp
      rawReading.value = d ? { ...result.raw, diagnostics: { ...result.raw.diagnostics,
        metrics: { ...result.raw.diagnostics.metrics, exp: { ...d, expProgress: progress } } } } : result.raw
      return
    }
    const previousTrustedGain = session.value.aggregate.expRateGained ?? 0
    sessionEngine.setExpSamplingInterval(Math.max(
      profile.value?.recognition.metricRecognitionIntervalsMs?.exp ?? 1_000,
      captureIntervalMs(),
      1_000 / (performanceConfigs[appliedPerformanceTier.value].processingFpsLimit || 8),
    ))
    sessionEngine.setExpReadoutInterval(Math.max(
      performanceConfigs[appliedPerformanceTier.value].metricRecognitionIntervalsMs.exp,
      performanceConfigs[appliedPerformanceTier.value].metricRecognitionIntervalsMs.level,
      captureIntervalMs(), pipeline?.samplingIntervalMs?.() ?? 0,
    ))
    restrictSessionRankForPerformance()
    const ingested = sessionEngine.ingestWithAccounting(result.state, result.events, result.observation, result.captureGeneration,
      profile.value?.recognition.minConfidence ?? .62)
    sessionExpCheckpoint = pipeline?.expCheckpoint() ?? sessionExpCheckpoint
    performanceDiagnostics.pipelineDelivery('ingested', result.state.capturedAt, result.captureGeneration)
    const expAccounting = ingested.accounting.exp
    performanceDiagnostics.recordingExp(result.observation.expExactCandidate?.sourceAt,
      ['contiguous', 'baseline', 'rebaseline'].includes(expAccounting.continuity) ? expAccounting.sourceAt : null)
    performanceDiagnostics.recordingEvent({ kind: 'exp-accounting', reason: expAccounting.continuity,
      sessionId: session.value.id, generation: captureGeneration })
    const displayExp = sessionEngine.expDisplayStatus()
    const rates = sessionEngine.liveRates(result.state.capturedAt)
    if (preset.value?.id === 'mxdc') performanceDiagnostics.observeExpStatisticsAdoption(`${captureGeneration}:${session.value.id}`,
      rates.expReadout?.scope === 'recovery' && rates.expReadout.status === 'ready' ? rates.expReadout.lastConfirmed?.sourceAt
        : displayExp.status === 'ready' && displayExp.netGain !== null ? displayExp.sourceAt : null)
    const progress = expRecognitionProgress.observe(`${captureGeneration}:${session.value.id}`, result.observation, displayExp, Date.now(), rates.expReadout)
    performanceDiagnostics.observeExpRecognition(session.value.id, progress)
    const expDiagnostic = result.raw.diagnostics.metrics?.exp
    const expAdmission = result.observation.expCandidateAdmission ?? result.observation.expEvidenceAdmission
    rawReading.value = expDiagnostic
      ? {
          ...result.raw,
          diagnostics: {
            ...result.raw.diagnostics,
            metrics: {
              ...result.raw.diagnostics.metrics,
              exp: {
                ...expDiagnostic,
                expProgress: progress,
                expAdmission: expAdmission ? { ...expAdmission,
                  creditedDelta: Math.max(0, (ingested.record.aggregate.expRateGained ?? 0) - previousTrustedGain) } : undefined,
                expDisplay: displayExp,
              },
            },
          },
        }
      : result.raw
    session.value = ingested.record
    journey.value = sessionEngine.journey()
    liveRates.value = rates
    confirmRecoveredExp()
  }

  async function acquireSessionStartBaseline(): Promise<GameStateSnapshot | null> {
    const targetPipeline = pipeline
    if (!targetPipeline) return null
    const requestedAt = Date.now()
    const generation = captureGeneration
    const controller = new AbortController()
    startBaselineController = controller
    const { signal } = controller
    const candidate = (): ExpExactCandidate | undefined => {
      const observation = latestObservation
      const sample = observation?.expExactCandidate
      if (latestObservationGeneration !== generation || !observation || !sample
        || sample.sourceAt > Date.now() || sample.value.estimated) return undefined
      const levelConfirmed = observation.accepted.level && observation.level.value === sample.level
        && sample.levelSourceAt !== null && sample.levelSourceAt <= sample.sourceAt
        && sample.sourceAt - sample.levelSourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
      return levelConfirmed ? sample : { ...sample, level: null, levelSourceAt: null }
    }
    const preview = candidate()
    const consensus = new SessionStartExpConsensus(requestedAt, profile.value?.recognition.minConfidence ?? .62,
      preview && requestedAt - preview.sourceAt <= SESSION_START_BASELINE_MAX_AGE_MS ? preview : undefined, true)
    const updateProgress = (stage: SessionSamplingStage = 'confirming') => {
      if (signal.aborted) return
      const elapsedMs = Date.now() - requestedAt
      const percentSamples = latestObservation?.expEstimatedAdmission?.samples ?? 0
      sessionStartProgress.value = { phase: elapsedMs >= 6_000 ? 'slow' : 'confirming', stage,
        slowReason: stage === 'waiting-frame' ? 'frame' : 'ocr', requestedAt, elapsedMs,
        currentMetric: 'exp', confirmedSamples: profile.value?.presetId === 'gcw' && latestObservation?.expPercentCandidate
          ? percentSamples >= 2 ? 2 : percentSamples === 1 ? 1 : 0 : consensus.sampleCount, targetSamples: 2,
        confirmationFailureReason: consensus.confirmationFailureReason }
    }
    updateProgress()
    const slowTimer = window.setTimeout(() => updateProgress(sessionStartProgress.value?.stage), 6_000)
    const stop = watch([captureStatus, ocrStatus, () => reloadSafety.preparing, profile], ([capture, ocr, reloading, currentProfile], previous) => {
      if (capture !== 'active') controller.abort('capture-unavailable')
      else if (ocr === 'error') { error.value = ocrError.value; controller.abort('engine-error') }
      else if (reloading) controller.abort('disposed')
      else if (currentProfile !== previous[3]) controller.abort('profile-changed')
    }, { flush: 'sync' })
    const cancelled = new Promise<null>(resolve => signal.addEventListener('abort', () => resolve(null), { once: true }))
    const collect = async (): Promise<GameStateSnapshot | null> => {
      while (!signal.aborted) {
        const sampled = await targetPipeline.sampleForSessionStart(captureService.video, ['level', 'exp'], signal, updateProgress)
        if (signal.aborted) return null
        if (pipeline !== targetPipeline || generation !== captureGeneration || !isCapturing.value) {
          controller.abort('capture-unavailable')
          return null
        }
        const result = consensus.observe(sampled ? candidate() : undefined)
        updateProgress()
        const percent = profile.value?.presetId === 'gcw' && latestObservationGeneration === generation
          && latestObservation?.accepted.level && latestObservation.expEstimatedAdmission?.status === 'accepted'
          ? latestObservation.expEstimatedEvidence : undefined
        const confirmed = percent && percent.level !== null && percent.level === latestObservation?.level.value
          && percent.sourceAt >= requestedAt && Date.now() - percent.sourceAt <= SESSION_START_BASELINE_MAX_AGE_MS
          && percent.levelSourceAt != null && Math.abs(percent.sourceAt - percent.levelSourceAt) <= SESSION_START_BASELINE_MAX_AGE_MS
          ? percent : result.confirmed
        const state = gameState.value
        if (confirmed && state) {
          const observation = latestObservation!
          const mesosFresh = observation.accepted.mesos && observation.mesos.value !== null
            && state.mesos === observation.mesos.value && state.observedAt?.mesos === observation.mesos.capturedAt
            && confirmed.sourceAt >= observation.mesos.capturedAt
            && confirmed.sourceAt - observation.mesos.capturedAt <= SESSION_START_BASELINE_MAX_AGE_MS
          return { ...state, capturedAt: confirmed.sourceAt, level: confirmed.level, exp: confirmed.value,
            mesos: mesosFresh ? state.mesos : null,
            observedAt: { ...state.observedAt!, level: confirmed.levelSourceAt ?? null, exp: confirmed.sourceAt,
              mesos: mesosFresh ? observation.mesos.capturedAt : null },
            confidence: { ...state.confidence, exp: confirmed.confidence } }
        }
        // The pipeline waits for independent source frames. Back off only when it made no progress.
        if (result.status === 'ignored') await new Promise<void>(resolve => {
          const finish = () => { window.clearTimeout(timer); signal.removeEventListener('abort', finish); resolve() }
          const timer = window.setTimeout(finish, 250)
          signal.addEventListener('abort', finish, { once: true })
        })
      }
      return null
    }
    let baseline: GameStateSnapshot | null = null
    try {
      baseline = await Promise.race([collect(), cancelled])
    } catch (caught) {
      if (!signal.aborted) {
        error.value = caught instanceof Error ? caught.message : String(caught)
        controller.abort('engine-error')
      }
    } finally {
      stop()
      window.clearTimeout(slowTimer)
      const reason = signal.reason
      if (reason === 'capture-unavailable' || reason === 'engine-error') {
        startIssue.value = { metric: null, reason, requestedAt }
      }
      if (!signal.aborted) controller.abort('complete')
      if (startBaselineController === controller) startBaselineController = null
      sessionStartProgress.value = null
      if (!baseline && !disposed && !reloadSafety.preparing && pipeline === targetPipeline && isCapturing.value) {
        targetPipeline.resume(captureService.video)
      }
    }
    return baseline
  }

  function dismissStartIssue(): void {
    startIssue.value = null
  }

  function cancelSessionStart(): void {
    startRequestId += 1
    cancelFirstUseWait?.()
    startBaselineController?.abort('user-cancelled')
    if (isStarting.value && automaticPresetController) automaticPresetController.abort('user-cancelled')
    sessionStartProgress.value = null
  }

  function setCaptureStartupPhase(phase: CaptureStartupPhase): void {
    captureStartupPhase.value = phase
    try {
      localStorage.setItem(CAPTURE_STARTUP_PHASE_KEY, JSON.stringify({ phase, recordedAt: Date.now() }))
    } catch {
      // Startup diagnostics are optional and never block capture.
    }
  }

  function yieldToMainThread(): Promise<void> {
    return new Promise((resolve) => window.setTimeout(resolve, 0))
  }

  function createProfile(gamePreset: GamePreset): CaptureProfile {
    const now = Date.now()
    return {
      id: crypto.randomUUID(),
      name: 'Default',
      presetId: gamePreset.id,
      presetVersion: gamePreset.version,
      ...(gamePreset.layoutVersion ? { layoutVersion: gamePreset.layoutVersion, autoRois: {} } : {}),
      sourceWidth: 1368,
      sourceHeight: 800,
      hudRect: gamePreset.hud.fallback,
      roiOverrides: {},
      colorCalibration: {},
      recognition: migrateRecognitionFrequencies({
        captureFps: DEFAULT_CAPTURE_FPS,
        metricRecognitionIntervalsMs: recommendedMetricRecognitionIntervalsMs(),
        metricRecognitionIntervalsVersion: METRIC_RECOGNITION_INTERVAL_VERSION,
        minConfidence: 0.62,
        ocrChangeThreshold: 0.025,
      }),
      createdAt: now,
      updatedAt: now,
    }
  }

  function ocrFingerprint(gamePreset: GamePreset): string {
    const models = gamePreset.remoteResources.ocrModels
    return `${models.detection.sha256}|${models.recognition.sha256}`
  }

  function captureSourceChanged(previous: CaptureProfile, next: CaptureProfile): boolean {
    return previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.attributeSources) !== JSON.stringify(next.attributeSources)
      || JSON.stringify(previous.roiOverrides) !== JSON.stringify(next.roiOverrides)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
  }

  function changedAdaptiveMetric(previous: CaptureProfile, next: CaptureProfile): RecognitionMetricKey | null {
    const hasNonRoiChange = previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
    if (hasNonRoiChange) return null
    const changed = recognitionMetricKeys.filter((metric) => JSON.stringify(previous.roiOverrides[metric]) !== JSON.stringify(next.roiOverrides[metric]))
    return changed.length === 1 ? changed[0] ?? null : null
  }

  return {
    captureRecoveryState, captureConfirmationSlow, captureConfirmationReason, captureEfficiencyWarming, retryCaptureRecovery,
    isInitializingCalibration, initializeCalibration,
    firstUseCalibrationPhase, firstUseCalibrationOpen, firstUseCalibrationProgress, firstUseCalibrationOutcome,
    firstUseCalibrationFrame, firstUseCalibrationRetained, firstUseCalibrationBlocking, firstUseCalibrationBusy,
    runFirstUseCalibration, skipFirstUseCalibration, closeFirstUseCalibration,
    recognitionPerformanceStatus, appliedPerformanceTier, captureFpsApplied, performanceChangeDeferred, captureFrequencyPending,
    preset, profile, session, liveRates, gameState, rawReading, hudLocation, captureStatus, hasSelectedWindowInPage, dashboardSponsorsDismissed, dashboardMesosHidden, dashboardResourcesHidden, captureLabel, captureFailure, captureDiagnostic, capturePreference, captureStartupPhase, error, needsCalibration,
    ocrStatus, ocrStage, ocrProgress, ocrCachedModels, ocrError, ocrErrorType, ocrStartupDiagnostic, capturePreparationDiagnostic, gameTypeChoice, gameTypeChoiceOpen, isChoosingGameType, dismissGameTypeChoice, retryGameTypeDetection, chooseGameType, changeCalibrationGameType, ocrCacheDegraded, journey, video, startIssue, sessionStartProgress,
    isCapturing, isSelectingWindow, canSelectWindow, captureSupported, captureCompatibilityIssue, isRunning, isPaused, isStarting, isEnding, isPreparingForReload, hasPendingFinalization,
    pipSupported, pipAvailability, pipIssue, isOpeningPip, pipDisabledReason, dismissPipIssue, refreshPipAvailability,
    initialize, loadPreset, selectOcrModel, isSwitchingOcrModel, ocrModelSwitchBlocked, selectWindow, startSession, pauseSession, resumeSession, endSession, updateProfile, setCalibrationMetric, openPip, initializeOcr, dismissStartIssue, cancelSessionStart,
  }
})
